module.exports={render:function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _vm._m(0)
},staticRenderFns: [function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _c('div', {
    staticClass: "chapter"
  }, [_c('div', {
    staticClass: "inner_page"
  }, [_c('h1', {
    staticClass: "chapter-no",
    attrs: {
      "id": "ch-3"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_40"
    }
  }, [_vm._v("3. Eschatological Visions and Symbols among Religions")])]), _c('a', {
    attrs: {
      "id": "page_40"
    }
  }, [_c('h1', {
    staticClass: "sec1",
    attrs: {
      "id": "sec-8"
    }
  }, [_c('strong', [_vm._v("On the Propriety and Conditions of Speaking of Religions’ “Eschatologies”")])])]), _c('p', {
    staticClass: "para1"
  }, [_c('a', {
    attrs: {
      "id": "page_40"
    }
  }, [_vm._v("Although the Christian theologian has to be careful when speaking of “eschatology” as a pan-religious theme, it is true that all world religions express a concern over mortality,"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft1",
      "id": "fn1"
    }
  }, [_vm._v("1")]), _vm._v(" and all of them also envision “some form of life after death.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft2",
      "id": "fn2"
    }
  }, [_vm._v("2")])]), _vm._v(" Furthermore, religions embrace beliefs not only about the origins but also about the “end” (or at least cycles of beginning and ending) of the whole of the cosmos."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft3",
      "id": "fn3"
    }
  }, [_vm._v("3")])]), _vm._v(" As diverse as these beliefs and symbols may be, it is clear that some kind of common denominator exists. That said, insofar as eschatology is “the study of the final end of things, the ultimate resolution of the entire creation,” then it applies much more easily to “theistic religions that hold to a doctrine of creation and a linear view of history and that believe that creation will come to a final end than to nontheistic traditions, particularly Buddhism.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft4",
      "id": "fn4"
    }
  }, [_vm._v("4")])]), _vm._v(" Recall that “Buddhist scriptures regularly refer to ‘beginningless "), _c('em', [_vm._v("sa"), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ṁ")]), _vm._v("sāra")]), _vm._v(",’ a cycle of birth and death of the universe (as well as of the individual) for which no starting point can be discerned. Nor is there an end, for Buddhists share with members of other Indian religions (notably the Hindus and the Jains) the idea that the universe passes "), _c('a', {
    attrs: {
      "id": "page_41"
    }
  }, [_vm._v("through an unending series of cycles of manifestation and nonmanifestation.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft5",
      "id": "fn5"
    }
  }, [_vm._v("5")]), _vm._v(" Add to that the general observation that “[c]ultures that view time as an endless succession of repetitive cycles … develop only ‘relative eschatologies,’ because the concept of an ultimate consummation of history is alien to them.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft6",
      "id": "fn6"
    }
  }, [_vm._v("6")])]), _vm._v(" Again, Buddhism is a grand example; this principle applies also to Hinduism."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft7",
      "id": "fn7"
    }
  }, [_vm._v("7")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("This project is supported by the vision expressed by the editors of "), _c('em', [_vm._v("Death and Immortality in the Religions of the World")]), _vm._v(", Paul and Linda Badham—one an atheist and the other a naturalist “atheist”: “[W]e remain hopeful that a global perspective on the issues of death and immortality may indeed emerge and present a coherent and intelligible account of a possible future hope, which will draw insights from both the religious and the secular experience of the human race.” An important asset in this search is the “unanimous testimony of the world religions that belief in an eternal destiny is necessary to any concept of human life serving any larger purpose than the fulfillment of the immediate aspirations we set ourselves.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft8",
      "id": "fn8"
    }
  }, [_vm._v("8")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("This investigation begins with Jewish and Islamic traditions and proceeds to study symbols and visions of the “end” in Hindu and Buddhist theologies. The discussion in this chapter refrains from making comparisons with Christian views in order to allow for authentic Jewish, Muslim, Hindu, and Buddhist eschatologies to be presented. The rest of the discussion of eschatology will engage sympathetically and critically specific beliefs and proposals of these traditions.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec1",
    attrs: {
      "id": "sec-9"
    }
  }, [_c('strong', [_vm._v("Jewish Eschatology")])]), _vm._v(" "), _c('h1', {
    staticClass: "sec2-t0",
    attrs: {
      "id": "sec2-14"
    }
  }, [_c('em', [_vm._v("The Gradual Emergence of Eschatological Consciousness")])]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_vm._v("Somewhat counterintuitively for a strictly monotheistic faith, no Old Testament books are devoted to the topic of death or afterlife; indeed, the topic is only marginally and occasionally mentioned."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft9",
      "id": "fn9"
    }
  }, [_vm._v("9")])]), _vm._v(" The Old Testament worldview "), _c('a', {
    attrs: {
      "id": "page_42"
    }
  }, [_vm._v("is very much this-worldly (although it is deeply "), _c('em', [_vm._v("theo")]), _vm._v("logical). One can hardly find incentives to look for the signs of the end in the Hebrew Bible; the rabbinic traditions have equally great reservations about that."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft10",
      "id": "fn10"
    }
  }, [_vm._v("10")]), _vm._v(" When eschatological themes appear occasionally among other key themes, they pertain less to individual and much more to national hope"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft11",
      "id": "fn11"
    }
  }, [_vm._v("11")])]), _vm._v(" and to Yahweh’s intervention in the cosmos. This-worldly blessings from Yahweh are at the center of the Israelite religion. Both Yahweh’s faithfulness in carrying out his promises"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft12",
      "id": "fn12"
    }
  }, [_vm._v("12")])]), _vm._v(" and Yahweh’s power are manifested in this life, and his blessings are counted in terms of long life,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft13",
      "id": "fn13"
    }
  }, [_vm._v("13")])]), _vm._v(" progeny, and possessions. In keeping with the focus of this-world spirituality, Yahweh’s role as judge is also by and large envisioned in relation to earthly affairs, particularly with the establishment of justice and righteousness."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft14",
      "id": "fn14"
    }
  }, [_vm._v("14")])]), _vm._v(" In sum: “In contrast to the elaborate myths and epics of other nations, the Israelites did not waste much thought on a life beyond.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft15",
      "id": "fn15"
    }
  }, [_vm._v("15")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("In stark contrast to neighboring lands, particularly Egypt, Israelite culture was definitely not death driven."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft16",
      "id": "fn16"
    }
  }, [_vm._v("16")])]), _vm._v(" The Old Testament describes the condition of the dead as some sort of shadowy existence (Isa. 38:18)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft17",
      "id": "fn17"
    }
  }, [_vm._v("17")])]), _vm._v(" The gradual rise of an eschatological consciousness in the Old Testament is just that, "), _c('em', [_vm._v("gradual")]), _vm._v(". The hope for an afterlife in terms of resurrection evolves slowly toward the end of the Old Testament,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft18",
      "id": "fn18"
    }
  }, [_vm._v("18")])]), _vm._v(" although intimations and anticipations appear here and there (Job 19:25–26; Pss. 49:15; 73:24; Isa. 26:19)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft19",
      "id": "fn19"
    }
  }, [_vm._v("19")])]), _vm._v(" Daniel 12:2 is widely taken as the summit of that development: “And many of those who "), _c('a', {
    attrs: {
      "id": "page_43"
    }
  }, [_vm._v("sleep in the dust of the earth shall awake, some to everlasting life, and some to shame and everlasting contempt.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft20",
      "id": "fn20"
    }
  }, [_vm._v("20")])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Only during the postexilic period can a “few inklings of otherworldliness” be found."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft21",
      "id": "fn21"
    }
  }, [_vm._v("21")])]), _vm._v(" The outlook changes radically in the theology of rabbinic Judaism. The Talmud and related writings delve deeply and widely into eschatological and apocalyptic speculations."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft22",
      "id": "fn22"
    }
  }, [_vm._v("22")])]), _vm._v(" Eschatology becomes a defining feature, and against that background it can be said “that no significant movement in the course of Jewish history had lacked an eschatology.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft23",
      "id": "fn23"
    }
  }, [_vm._v("23")])]), _vm._v(" Whence the rise of an eschatological orientation? I am persuaded by David Novak’s thesis that the major reason for the evolvement of eschatology in the Old Testament, slow as it was, has to do with the question of suffering and theodicy: Why is the believer in Yahweh suffering?"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft24",
      "id": "fn24"
    }
  }, [_vm._v("24")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("With the rise of the eschatological impulse (beginning in the second century "), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("B.C.E.")]), _vm._v("), the hope for the afterlife also becomes more defined, as death is no longer looked upon as “the final event” in human life."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft25",
      "id": "fn25"
    }
  }, [_vm._v("25")])]), _vm._v(" In rabbinic thought, eschatological topics can be classified in three mutually related categories: “(1) the world to come ("), _c('em', [_vm._v("olam ha-ba")]), _vm._v("); (2) the resurrection of the dead human body ("), _c('em', [_vm._v("tehiyyat ha-metim")]), _vm._v("); (3) the Messiah ("), _c('em', [_vm._v("ha-meshiah")]), _vm._v(").” Although the communal orientation still is dominant, under these categories one can see also hope for the individual—and for the whole cosmos (“the "), _c('em', [_vm._v("world")]), _vm._v(" to come”). The interrelatedness of these categories comes to the fore in that—particularly in early Pharisaic-rabbinic traditions—they were more or less conflated: the Messiah would bring the world renewal and promise the resurrection."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft26",
      "id": "fn26"
    }
  }, [_vm._v("26")])]), _vm._v(" This kind of “eschatology is manifested in the expectation of a future eon radically discontinuous with the present.” Yet this means no escapism, because in this eschatological vision “the circumstances of history will be transformed but not transcended.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft27",
      "id": "fn27"
    }
  }, [_vm._v("27")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_c('a', {
    attrs: {
      "id": "page_44"
    }
  }, [_vm._v("In very broad strokes, Jews came to believe that, at the end of days, the dead will be resurrected and come before God to account for their lives on earth; the righteous will be rewarded and the evil punished; Jews, free from the yoke of the exile, will return to their homeland, rebuild it, and become masters of their own destiny; they will rebuild the temple and reinstitute the temple cult; the nations of the world will flock to study Torah with the Jewish people; peace and justice will rule; the tensions that now pervade the world of nature will disappear—“the wolf shall dwell with the lamb” (Isa. 11:6); and all people will come to know and worship the God of Israel. Finally, this entire scenario will be brought to pass through the initiative of the charismatic or quasi-divine figure called Messiah (literally, “the anointed one”)."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft28",
      "id": "fn28"
    }
  }, [_vm._v("28")])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Like younger sister faiths, postbiblical Judaism also knows apocalypticism, which is escapist."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft29",
      "id": "fn29"
    }
  }, [_vm._v("29")])]), _vm._v(" Some of these movements were also related to Christian ones."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft30",
      "id": "fn30"
    }
  }, [_vm._v("30")])]), _vm._v(" The difference of this apocalypticism from the Old Testament eschatology has to do with the heightened role given to the Messiah in end-time expectations (indeed, it is quite counterintuitive that by and large the Messiah is not tightly linked to eschatology in the Old Testament)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft31",
      "id": "fn31"
    }
  }, [_vm._v("31")])]), _vm._v(" Rather than Yahweh himself executing eschatological works (as even in Daniel’s apocalypse), in the postbiblical apocalypticism ("), _c('em', [_vm._v("1 Enoch")]), _vm._v(", "), _c('em', [_vm._v("2 Esdras")]), _vm._v(", and "), _c('em', [_vm._v("2 Baruch")]), _vm._v(") the Messiah is the one at the forefront of the action. Another defining feature of apocalypticism has to do with a changing understanding of history. Not only Israel’s enemies (as in the Old Testament) but all powers, including cosmic and heavenly powers, would be destroyed. This leads to the apocalyptic “supranationalistic and supraworldly view of God’s reign” that also assumes the unity of history—because Yahweh is the one and only ultimate power. That concept also leads to a deterministic view of history."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft32",
      "id": "fn32"
    }
  }, [_vm._v("32")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Another development, outside the mainstream although hugely influential, is the mystical Kabbalah’s visions of the end."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft33",
      "id": "fn33"
    }
  }, [_vm._v("33")])]), _vm._v(" Kabbalah also tested "), _c('a', {
    attrs: {
      "id": "page_45"
    }
  }, [_vm._v("and at times transcended the limits of orthodoxy."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft34",
      "id": "fn34"
    }
  }, [_vm._v("34")]), _vm._v(" A particularly important case here is the doctrine of metempsychosis, or the transmigration of souls (or reincarnation)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft35",
      "id": "fn35"
    }
  }, [_vm._v("35")])]), _vm._v(" Although unknown in rabbinic and Talmudic literature—and strongly opposed by the medieval philosophical masters—it has always had some following among the mystics, coupled with the doctrine of the preexistence of souls."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft36",
      "id": "fn36"
    }
  }, [_vm._v("36")])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-15"
    }
  }, [_c('em', [_vm._v("Salvation and Condemnation—Heaven and Hell")])]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_vm._v("There seem to be two kinds of attitudes in Jewish tradition concerning speculation into the nature of the world to come. As in other traditions, eschatological hermeneutics often takes its departure from the earthly scene, such as the description of David’s encounter with the angel of death in the garden in the hereafter."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft37",
      "id": "fn37"
    }
  }, [_vm._v("37")])]), _vm._v(" On the other hand, both Scripture and rabbinical tradition make the realities of the hereafter so incomparable with the current world that we can hardly say anything about those realities. An illustrative statement comes from Rabbi Hiyya ben Abba (speaking in the name of Rabbi Johanan): “All the prophets prophesied only for the days of the Messiah, but as for the world to come, ‘Eye hath not seen, oh God, beside Thee.’ ”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft38",
      "id": "fn38"
    }
  }, [_vm._v("38")])]), _vm._v(" Understandably, then, the Talmudic tradition often speaks of the life to come in terms of radical discontinuity."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft39",
      "id": "fn39"
    }
  }, [_vm._v("39")])]), _vm._v(" Indeed, with all its this-worldly orientation, classic Jewish theology has developed quite sophisticated accounts of heaven, including several stages from the messianic age on earth to the transcendent heavenly realm."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft40",
      "id": "fn40"
    }
  }, [_vm._v("40")])]), _vm._v(" Perhaps the most detailed one can be found in the Babylonian Talmud’s "), _c('em', [_vm._v("Eiruvin")]), _vm._v(" (19a), with its sevenfold structure of hell."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft41",
      "id": "fn41"
    }
  }, [_vm._v("41")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Who then will inherit salvation? Who will be condemned? The Jewish understanding of what is wrong with humanity is markedly different from "), _c('a', {
    attrs: {
      "id": "page_46"
    }
  }, [_vm._v("the Christian one; hence, the way of reaching salvation is different."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft42",
      "id": "fn42"
    }
  }, [_vm._v("42")]), _vm._v(" Like the Muslim view, it insists on human freedom and responsibility."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft43",
      "id": "fn43"
    }
  }, [_vm._v("43")])]), _vm._v(" Following the Torah is the key."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft44",
      "id": "fn44"
    }
  }, [_vm._v("44")])]), _vm._v(" “The principle qualification for entrance to heaven ("), _c('em', [_vm._v("Gan Eden")]), _vm._v(") is to lead a good life in accordance with God’s law. Conversely … by disobeying God’s law one forfeits a share in the world to come and is doomed to eternal punishment in hell ("), _c('em', [_vm._v("Gehinnom")]), _vm._v(").”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft45",
      "id": "fn45"
    }
  }, [_vm._v("45")])]), _vm._v(" Rabbinic theology mentions a number of types of people who might end up in hell, including those who deny resurrection or Torah’s heavenly origin, or the heretic, or the one who abuses the divine name, and so forth."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft46",
      "id": "fn46"
    }
  }, [_vm._v("46")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("This much almost all Jewish traditions agree upon—keeping Torah and doing good deeds. Debated issues have to do with two topics: First, what is the balance between good and bad deeds? In other words, is there a turning point beyond which one more evil deed may tip the scales? According to Novak, it is best “to engage in constant repentance both in one’s heart (faith) and in one’s deeds (good works)” because it is not possible to accumulate “merit in this world so as to ‘cash in’ in the world to come.… Talmud points out that the world to come can be gained in a moment … or lost in a moment.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft47",
      "id": "fn47"
    }
  }, [_vm._v("47")])]), _vm._v(" The second issue is whether Torah’s commandments will be kept even in the world to come. These discussions centered on the interpretation of the command in Deuteronomy 16:3, “that all the days of your life you may remember the day when you came out of the land of Egypt.” Put in terms of Christian theology, will the divinely sanctioned covenant-related salvation-historical commandments end or continue after the boundary of this world? Both views received authoritative support, though for the purposes of this discussion, the details do not interest us."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft48",
      "id": "fn48"
    }
  }, [_vm._v("48")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Yet an important long-standing, unresolved issue of debate relates to the destiny of Gentiles. Not surprisingly, no canonical opinion was reached among the different rabbinical schools."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft49",
      "id": "fn49"
    }
  }, [_vm._v("49")])]), _vm._v(" This much can be said: while the "), _c('a', {
    attrs: {
      "id": "page_47"
    }
  }, [_vm._v("Christian type of "), _c('em', [_vm._v("extra ecclesiam nulla salus")]), _vm._v(" principle was often held as the normal opinion, there is also undoubtedly a strong prophetic tradition in the Old Testament that envisions some kind of “universal scope of salvation.” Rather than leading to ultimate destruction and annihilation, Yahweh’s judgment will have shalom as the final word (Amos 9:11–15; Isa. 2:2–4; 11:6–9; 65:17–25; and so forth)."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft50",
      "id": "fn50"
    }
  }, [_vm._v("50")]), _vm._v(" But even then, no unanimity exists about whether “nations” (as opposed to the “people” of Yahweh) are being included in this salvation, which also encompasses nature and the whole world.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Broadly speaking, until modernity, the core rabbinic belief in two destinies stood intact (regardless of continuing debates about whom to include or exclude)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft51",
      "id": "fn51"
    }
  }, [_vm._v("51")])]), _vm._v(" In contrast, the majority of modern and contemporary Jews have left it behind or at least qualified it significantly. This is possible because the Torah, as is well known, explains quite little about the nature of judgment and blessedness beyond this world. Although the opinions of rabbis and the Mishnah are formative, they are not necessarily binding in the sense that one could not be a faithful Jew while rejecting them. As for Christian theology, particularly difficult for contemporary Jewish theologians is the affirmation of hell."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft52",
      "id": "fn52"
    }
  }, [_vm._v("52")])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-16"
    }
  }, [_c('em', [_vm._v("The Resurrection of the Body")])]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_vm._v("Although “the canonization” of the doctrine happened only three centuries after Daniel,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft53",
      "id": "fn53"
    }
  }, [_vm._v("53")])]), _vm._v(" according to David Novak, “the centerpiece of classical Jewish eschatology is the doctrine of the resurrection of the dead.” He takes as strong evidence the prayer at the center of daily Jewish liturgy, in which we find the clause “Blessed are you, O Lord, who resurrects the dead.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft54",
      "id": "fn54"
    }
  }, [_vm._v("54")])]), _vm._v(" Indeed, condemned are those who deny the centrality of this belief."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft55",
      "id": "fn55"
    }
  }, [_vm._v("55")])]), _vm._v(" Briefly stated, resurrection and the authority of Torah “are the two dogmas the rabbis required, "), _c('a', {
    attrs: {
      "id": "page_48"
    }
  }, [_vm._v("minimally, that no Jew deny and, maximally, that every Jew affirm.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft56",
      "id": "fn56"
    }
  }, [_vm._v("56")]), _vm._v(" That is also affirmed by the medieval master Moses Maimonides."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft57",
      "id": "fn57"
    }
  }, [_vm._v("57")])]), _vm._v(" That said, there were of course dissenting voices within Jewish orthodoxy: while the Pharisees fully endorsed the doctrine, the Sadducees did not."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft58",
      "id": "fn58"
    }
  }, [_vm._v("58")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("For rabbinic theology, it was not enough to merely affirm immortality (whether that be understood as an innate capacity of the soul or a divine gift);"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft59",
      "id": "fn59"
    }
  }, [_vm._v("59")])]), _vm._v(" a "), _c('em', [_vm._v("bodily")]), _vm._v(" resurrection must also be affirmed. This means a robust affirmation of the principle of embodiment and physicality as an integral part of human nature. This comports well with Jewish anthropology at large (as discussed in detail in "), _c('em', [_vm._v("Creation and Humanity")]), _vm._v(", chap. 14), which is neither monist in the materialist sense nor dualist in the traditional sense. It rather approaches the Thomistic hylomorphism in which the soul is the “form” of the body; neither one can exist without the other."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft60",
      "id": "fn60"
    }
  }, [_vm._v("60")])]), _vm._v(" In their attempts to imagine the nature of the bodily resurrection, the Jewish sources use various metaphors, from awakening from sleep,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft61",
      "id": "fn61"
    }
  }, [_vm._v("61")])]), _vm._v(" to nature metaphors of morning dew and plants sprouting,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft62",
      "id": "fn62"
    }
  }, [_vm._v("62")])]), _vm._v(" to being clothed,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft63",
      "id": "fn63"
    }
  }, [_vm._v("63")])]), _vm._v(" and so forth. Some New Testament Christian images of course come to mind.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-17"
    }
  }, [_c('em', [_vm._v("Afterlife, Resurrection, and Immortality in Contemporary Jewish Theology")])]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_vm._v("By and large, modern/contemporary Jewish theologies have not paid much attention to end times."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft64",
      "id": "fn64"
    }
  }, [_vm._v("64")])]), _vm._v(" Novak wonders if one of the reasons for reservation "), _c('a', {
    attrs: {
      "id": "page_49"
    }
  }, [_vm._v("has to do with the painful memories from medieval times when Jews were forced by Christians to defend their eschatological views in public."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft65",
      "id": "fn65"
    }
  }, [_vm._v("65")]), _vm._v(" Another obvious reason is the Holocaust; many Jews wonder if there is any afterlife after Auschwitz."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft66",
      "id": "fn66"
    }
  }, [_vm._v("66")])]), _vm._v(" On the other end of spectrum are radical millenarian and apocalyptic expressions similar to those of other Abrahamic faiths."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft67",
      "id": "fn67"
    }
  }, [_vm._v("67")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("When contemporary theologies have touched on eschatology, quite radical reformulations have been suggested; they usually follow the ordinary lines of distinction on the continuum from most traditional/conservative to reconstructionist views. Formative early influence came from the leading modern thinker Moses Mendelssohn and his highly acclaimed "), _c('em', [_vm._v("Phaedo; or, On the Immortality of the Soul, in Three Dialogues")]), _vm._v(" (1767)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft68",
      "id": "fn68"
    }
  }, [_vm._v("68")])]), _vm._v(" Obviously based on Plato’s dialogue, it reworks his ideas into an emerging modern Jewish milieu. Making Socrates a person of the Enlightenment, Mendelssohn argues for the immortality of the soul and its indestructibility because of its noncomposite nature. Wisely, though, Mendelssohn avoids any talk about the nature of life hereafter, including silence on resurrection, although he clearly seems to believe in the continuation of life."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft69",
      "id": "fn69"
    }
  }, [_vm._v("69")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Wide influence on modern and contemporary Jewish eschatology came also from the Reform movements’ reworking of key doctrines beginning in the early nineteenth century. Deeply Jewish, they also wanted to save Judaism for modern times. Slowly and steadily resurrection as a doctrine with “no religious foundation” came to be replaced by the idea of immortality, which also continued asserting “the divine nature of the human spirit.” Along with resurrection went the classic doctrines of hell and heaven; not surprisingly, Mendelssohn’s legacy was appealed to."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft70",
      "id": "fn70"
    }
  }, [_vm._v("70")])]), _vm._v(" Understandably, opposition arose among the conservatives. However, even Conservative Judaism of the early twentieth century did not necessarily demand a return to resurrection; rather, it insisted on a "), _c('a', {
    attrs: {
      "id": "page_50"
    }
  }, [_vm._v("sort of spiritual immortality. In other words, by the mid-twentieth century, immortality had been adopted as the mainstream Jewish opinion."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft71",
      "id": "fn71"
    }
  }, [_vm._v("71")])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("With the Reconstructionist movement headed by Mordecai Kaplan, a radically new paradigm was offered. His religious naturalism took religion as a natural phenomenon rather than as divinely revealed. As a consequence, the “Modification of Traditional Doctrines” argued that “[m]en and women brought up in the atmosphere of modern science no longer accept the doctrine that the dead will one day come to life.” Beliefs in the immortality and the indestructible nature of the human spirit should be given up. Even though the revised Reconstructionist prayer books continued to mention the immortality of the soul (but not resurrection of the body), it should be understood “in terms that are in keeping with what modern-minded men can accept as true.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft72",
      "id": "fn72"
    }
  }, [_vm._v("72")])]), _vm._v(" On the other end of the spectrum remained Orthodox Judaism, which firmly continued to uphold the doctrine of the resurrection of the body."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft73",
      "id": "fn73"
    }
  }, [_vm._v("73")])]), _vm._v(" In sum, the core classic doctrine of the resurrection of the body became the dividing line for modern Jews in a highly ironic manner: “[C]ontemporary Orthodoxy embraces resurrection and liberal Jews reject it "), _c('em', [_vm._v("for the very same reason")]), _vm._v(": They all understand it as a literal statement. All of these thinkers believe that the doctrine can only be interpreted as describing, in literal, objective terms, an event that will occur exactly as described in some future time. That is why Orthodox Jews affirm it and liberal Jews reject it.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft74",
      "id": "fn74"
    }
  }, [_vm._v("74")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Rightly, Neil Gillman wonders why contemporary Jewish theology got stuck with this literalist understanding. Why not look for alternative ways to affirm the classic doctrine? Furthermore, why does liberal and Reconstructionist Jewish thinking seem not even interested in pursuing possible promises of a contemporary constructive doctrine of physical resurrection?"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft75",
      "id": "fn75"
    }
  }, [_vm._v("75")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Not all contemporary Jewish intellectuals are content with this conservative-liberal divide. New interest in pursuing the questions of the afterlife has emerged from surprising circles. The former Marxist Will Herberg’s widely discussed "), _c('em', [_vm._v("Judaism and Modern Man")]), _vm._v(" (1951) tapped into not only his own religious background and those of some leading twentieth-century Jewish thinkers (particularly Franz Rozenweig) but also Christian sources in its search of eschatology beyond naturalist materialism. Whereas the Marxist “secularization of the Hebraic ‘philosophy’ of history … in which human existence "), _c('a', {
    attrs: {
      "id": "page_51"
    }
  }, [_vm._v("is reduced to the two dimensions of nature and society and deprived of its transcendent dimension of spirit” makes history “its own redeemer,” in Jewish faith “[t]he fulfilment of history is the Kingdom of Heaven ("), _c('em', [_vm._v("malkut shamayim")]), _vm._v(").” This kingdom is both this-worldly and transcendent, meaning that “it is not a kingdom in a kind of superworld called ‘Heaven,’ but a new age in which the kingship of God is revealed in its fullness and sovereignty.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft76",
      "id": "fn76"
    }
  }, [_vm._v("76")])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Some other leading Jewish thinkers, similarly to Herberg, have been persuaded of the necessity for belief in the bodily resurrection, as difficult as that may be for the modern mind. Prominent among these is Arthur A. Cohen, whose short essay “Resurrection of the Dead” seeks to rehabilitate the doctrine as an indispensable tenet of faith for contemporary Judaism. As much as it is “scandalous to reason,”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft77",
      "id": "fn77"
    }
  }, [_vm._v("77")])]), _vm._v(" resurrection is also a profound sign of divine hospitality."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft78",
      "id": "fn78"
    }
  }, [_vm._v("78")])]), _vm._v(" Somewhat surprisingly, and more recently, the “liberal” Reform movement has harked back to affirming in one way or another the continuation of life beyond death. Representative is Rabbi Eugene B. Borowitz’s conclusion that “we cannot believe that having shared so intimately in God’s reality in life we do not continue to share it beyond the grave.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft79",
      "id": "fn79"
    }
  }, [_vm._v("79")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Perhaps the theologically most pregnant contemporary affirmation of the resurrection of the body comes from Neil Gillman, a leading American Jewish thinker. Honestly facing the enormous intellectual problems that belief in the resurrection creates for the modern/contemporary post-Enlightenment scientific mind, he finds two powerful reasons for refusing to let the critical mind "), _c('em', [_vm._v("alone")]), _vm._v(" dictate one’s ultimate vision. On the one hand, with every other Jew, he is fully convinced of the omnipotence of God, even over death. Along with this theological argument, on the other hand, the anthropological argument seems convincing: the biblical view of the human as a “psycho-physical unity” makes him believe that without the body his own self is not complete."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft80",
      "id": "fn80"
    }
  }, [_vm._v("80")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("What about the eschatological vision of the youngest Abrahamic tradition? To the consideration of Islamic theology of the end we turn next.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec1",
    attrs: {
      "id": "sec-10"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_52"
    }
  }, [_c('strong', [_vm._v("Islamic Eschatology")])])]), _c('a', {
    attrs: {
      "id": "page_52"
    }
  }, [_c('h1', {
    staticClass: "sec2-t0",
    attrs: {
      "id": "sec2-18"
    }
  }, [_c('em', [_vm._v("The Significance of Eschatology in Islam")])])]), _c('p', {
    staticClass: "para1"
  }, [_c('a', {
    attrs: {
      "id": "page_52"
    }
  }, [_vm._v("Eschatology, both personal and universal, plays an extraordinary role in Islam."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft81",
      "id": "fn81"
    }
  }, [_vm._v("81")]), _vm._v(" Recall that the Prophet’s first and continuing message was about coming judgment and the need for submission to Allah to avoid the hell of judgment."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft82",
      "id": "fn82"
    }
  }, [_vm._v("82")])]), _vm._v(" The lack of positive response in Mecca only intensified the prophetic warnings."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft83",
      "id": "fn83"
    }
  }, [_vm._v("83")])]), _vm._v(" Both the Qur’an"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft84",
      "id": "fn84"
    }
  }, [_vm._v("84")])]), _vm._v(" and particularly Hadith"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft85",
      "id": "fn85"
    }
  }, [_vm._v("85")])]), _vm._v(" texts go to great lengths in expositing and describing (often with passionate and imaginative pictures) the events in the afterlife."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft86",
      "id": "fn86"
    }
  }, [_vm._v("86")])]), _vm._v(" Consider also the prominence of eschatological beliefs in Muslim creedal statements. Take, for example, the “Hanbalī Traditionalist Creed” after the legacy of (if not even penned by) Ahmad ibn Hanbal of the ninth century. Focused on true doctrine of God and proper lifestyle of the faithful ones, it is also rich with references to eschatological events and figures, including “the one-eyed Dajjal” (a traditional description of the Antichrist), end-time trumpets, paradise, elaborate descriptions of heaven, and so forth."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft87",
      "id": "fn87"
    }
  }, [_vm._v("87")])]), _vm._v(" That the densest presentation of core Islamic doctrines can be summarized under the following three headings speaks to the significance of eschatology: the unity of God, “prophecy,” and “ultimate ‘return’ (ma’ād).”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft88",
      "id": "fn88"
    }
  }, [_vm._v("88")])]), _vm._v(" The idea of “return” to God is based on Qur’an 7:29: “As He brought you into being, so return ye [unto Him].”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft89",
      "id": "fn89"
    }
  }, [_vm._v("89")])]), _vm._v(" No wonder contemporary Muslims tend to take the traditional teaching on eschatology much more seriously than do most Jews "), _c('a', {
    attrs: {
      "id": "page_53"
    }
  }, [_vm._v("and Christians."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft90",
      "id": "fn90"
    }
  }, [_vm._v("90")]), _vm._v(" The “eschatological narrative” lays claim on everything in the Muslim’s faith and life."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft91",
      "id": "fn91"
    }
  }, [_vm._v("91")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("As in other Abrahamic faiths, not only is purposefulness discerned in human life and events, but also “the Qur’ān posits an understanding of meaning and significance to the flow of time and history.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft92",
      "id": "fn92"
    }
  }, [_vm._v("92")])]), _vm._v(" There is a definite divinely set plan that moves history from creation to the consummation. Furthermore, similarly to Judaism and Christianity, a rich tradition of apocalypticism can be found in Islam. Not confined to one Muslim community, it is present in every denomination."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft93",
      "id": "fn93"
    }
  }, [_vm._v("93")])]), _vm._v(" Understandably, some extreme forms of apocalypticism drawn to millenarian and revolutionary impulses have arisen, some of which have received a lot of publicity (particularly those of the jihadist slant)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft94",
      "id": "fn94"
    }
  }, [_vm._v("94")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Fascination with eschatological topics is also present in the ethnic and cultural adaptations of Islam in new contexts such as China. Routinely called the “father” of Chinese-language Islam, Wang Daiyu of the seventeenth century, in his book on how to understand and define "), _c('em', [_vm._v("tawhid")]), _vm._v(" (the oneness of God) in relation to traditional Chinese terms, delves deeply into cosmogony, “the ten thousand things,” regarding both the origins and destiny."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft95",
      "id": "fn95"
    }
  }, [_vm._v("95")])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-19"
    }
  }, [_c('em', [_vm._v("On Discerning the Signs of the “Hour”")])]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_vm._v("As in Christian tradition, in Islam the (final) hour is unknown to all but God (Q 31:34)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft96",
      "id": "fn96"
    }
  }, [_vm._v("96")])]), _vm._v(" The Prophet was asked every now and then to reveal the hour of the end. Whenever that happened,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft97",
      "id": "fn97"
    }
  }, [_vm._v("97")])]), _vm._v(" the Prophet responded, “Knowledge thereof lies "), _c('a', {
    attrs: {
      "id": "page_54"
    }
  }, [_vm._v("only with God—and what do you know, perhaps the Hour is near” (Q 33:63)."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft98",
      "id": "fn98"
    }
  }, [_vm._v("98")]), _vm._v(" Differently from Hadith and apocalyptic traditions, the Qur’an is reticent to talk about signs—and somewhat counterintuitively seems to imply that indeed all the signs have already happened: “Do they, then, await anything except that the Hour should come upon them suddenly? For already its portents have come. So, when it has come upon them, for what [benefit] will their reminder be?”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft99",
      "id": "fn99"
    }
  }, [_vm._v("99")])]), _vm._v(" That said, eschatological undergirding lies beneath a number of suras in the Qur’an. Just think of titles such as “The Hour” (22), “The Smoke” (45), “The Darkened Sun” (82), and “The Shattering of the Sky” (84), among others."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft100",
      "id": "fn100"
    }
  }, [_vm._v("100")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Understandably, Muslims have not stopped looking for signs."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft101",
      "id": "fn101"
    }
  }, [_vm._v("101")])]), _vm._v(" Books, blogs, and talks on “signs of the hour” abound."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft102",
      "id": "fn102"
    }
  }, [_vm._v("102")])]), _vm._v(" The search for the signs is fueled by the presence in the Hadith of detailed lists of signs. Consider the vivid descriptions in the famous Abu-Dawudh’s “Trials and Fierce Battles”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft103",
      "id": "fn103"
    }
  }, [_vm._v("103")])]), _vm._v(" or in "), _c('em', [_vm._v("Sahih Muslim")]), _vm._v("’s “Book Pertaining to the Turmoil and Portents of the Last Hour” (Kitab Al-Fitan wa Ashrat As-Sa"), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ʿ")]), _vm._v("ah)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft104",
      "id": "fn104"
    }
  }, [_vm._v("104")])]), _vm._v(" “Geological, moral, social, and cosmic signs … [as well as] the erosion of the earth, the spread of immorality, the loss of trust among the people, and the administration of unjust rulers [are perceived] as some signs of the Hour.” In distinction from these “minor” signs, the Hadith lists as “major” ones the “emergence of the Antichrist, the descent of Jesus, and the rising of the sun in the west,” which all point to the imminence of the end. Quite similarly to the descriptions in the book of Revelation, trumpets, archangels, and cataclysmic changes on earth, including earthquakes, play a role in the final consummation (Q 81:1–14; 99:1–4; 39:67–69); there will also be intense suffering by the unfaithful. The rise of the mysterious nations of Gog and Magog also plays a role in the eschatological scheme of Islam."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft105",
      "id": "fn105"
    }
  }, [_vm._v("105")])]), _vm._v(" The descriptions of doomsday, again, are similar to those in the two sister Abrahamic traditions. The resurrection of all will happen, of both good and evil (Q 18:99; 36:51)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft106",
      "id": "fn106"
    }
  }, [_vm._v("106")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_c('a', {
    attrs: {
      "id": "page_55"
    }
  }, [_vm._v("While these eschatological sayings and traditions abound in folk Islam and popular culture in Muslim lands, contemporary Muslim theologians face similar kinds of hermeneutical challenges that Christians face. Should these “prophecies” be interpreted literally?"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft107",
      "id": "fn107"
    }
  }, [_vm._v("107")]), _vm._v(" If so, then the Enlightenment-based modernist critique must be rebutted. If not, they cannot be attributed properly to the Prophet himself, in which case they can be dismissed. After all, the discussion about authenticity is an important hermeneutical task for Islam."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft108",
      "id": "fn108"
    }
  }, [_vm._v("108")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Islam’s indebtedness to Jewish-Christian tradition comes to the fore even in eschatology, as the next section will demonstrate. Therein the awaited figure of the Mahdi and his companion Jesus play a key role.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-20"
    }
  }, [_c('em', [_vm._v("Mahdi, Jesus, and the Antichrist: The Major End-Time Figures")])]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_vm._v("Although Islam is not a messianic religion in the sense that Judaism and Christianity are, Muslim eschatology widely embraces the figure of the Mahdi, who undoubtedly reflects messianic characteristics."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft109",
      "id": "fn109"
    }
  }, [_vm._v("109")])]), _vm._v(" The task of the Mahdi is to defeat the Antichrist and bring justice and peace to the world and lead people to truth."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft110",
      "id": "fn110"
    }
  }, [_vm._v("110")])]), _vm._v(" An ordinary human being rather than a divine figure—according to the tradition, coming from the family of the Prophet himself—the Mahdi is endowed supernaturally to accomplish his task."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft111",
      "id": "fn111"
    }
  }, [_vm._v("111")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("It is highly surprising that the Mahdi is unknown not only in the Qur’an"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft112",
      "id": "fn112"
    }
  }, [_vm._v("112")])]), _vm._v(" but also in the two main Hadith traditions, that of Bukhari and that of Muslim. Yet belief in the Mahdi is affirmed widely in all main denominations. Particularly important is the role of the Mahdi among the Shi’ites."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft113",
      "id": "fn113"
    }
  }, [_vm._v("113")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Debate persists about a number of issues related to the Mahdi, not least as to whether there is one Mahdi or more than one, as well as the related "), _c('a', {
    attrs: {
      "id": "page_56"
    }
  }, [_vm._v("question of pseudo- (or anti-) Mahdi."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft114",
      "id": "fn114"
    }
  }, [_vm._v("114")]), _vm._v(" Not surprisingly, various Mahdist movements throughout history have tended to identify a specific person; the last significant movement was Sudanese, acclaiming as Mahdi Muhammad Ahmad ibn ‘Abd Allāh (d. 1885)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft115",
      "id": "fn115"
    }
  }, [_vm._v("115")])]), _vm._v(" The relationship between the Mahdi and Jesus is close—indeed, so close that, at times, particularly in early Islamic history, the two were confused to the point that the expectation of the coming of Mahdi ceased."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft116",
      "id": "fn116"
    }
  }, [_vm._v("116")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Whatever else the Mahdi tradition may mean to Islamic eschatology, it is linked closely with the yearning for justice and righteousness in a world of evil. Hence, at times those historical figures prominent for fighting injustice have been identified as the Mahdi, including Abdullah bin al-Zubair, a companion of the Prophet himself in the struggle against some Umayyad Dynasty leaders; similarly, the famed Umayyad leader Umar II of the eight century was discerned as one."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft117",
      "id": "fn117"
    }
  }, [_vm._v("117")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Along with the Mahdi, Jesus plays an important role in Muslim eschatology in particular. As detailed in "), _c('em', [_vm._v("Christ and Reconciliation")]), _vm._v(" (chaps. 10 and 15), Jesus is next to only Muhammad himself; hence, his role as the Messenger, a prophet, is an integral part of the Muslim confession of faith—although, of course, without any hint of divinity."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft118",
      "id": "fn118"
    }
  }, [_vm._v("118")])]), _vm._v(" While the Qur’an itself does not directly mention Jesus’ return to earth"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft119",
      "id": "fn119"
    }
  }, [_vm._v("119")])]), _vm._v("—or, as it is often called, his “descent”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft120",
      "id": "fn120"
    }
  }, [_vm._v("120")])]), _vm._v("—it is widely attested in the Hadith (with about one hundred reliable references)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft121",
      "id": "fn121"
    }
  }, [_vm._v("121")])]), _vm._v(" According to the standard Islamic interpretation, Jesus of Nazareth was not killed on the cross but was instead “taken up” by Allah to heaven to wait for the return. Then he will fight with the Mahdi against the Antichrist and defeat him."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft122",
      "id": "fn122"
    }
  }, [_vm._v("122")])]), _vm._v(" “Then Jesus kills the pigs, destroys the crosses (since the Christians have adopted a forbidden cult by honoring the cross), tears down the Synagogues and Churches, as well as kills the Christians who are not willing to receive the true faith.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft123",
      "id": "fn123"
    }
  }, [_vm._v("123")])]), _vm._v(" Importantly, the descent of Jesus is "), _c('a', {
    attrs: {
      "id": "page_57"
    }
  }, [_vm._v("considered one of the surest signs of the “Hour” ("), _c('em', [_vm._v("al-Sa’a")]), _vm._v("), the consummation of eschatological events in Islam.")])]), _c('a', {
    attrs: {
      "id": "page_57"
    }
  }), _c('p', {
    staticClass: "indent-para"
  }, [_c('a', {
    attrs: {
      "id": "page_57"
    }
  }, [_vm._v("Notwithstanding the lack of direct references to the descent of Jesus in the Qur’an, some key statements about his work and meaning have important eschatological implications, such as 3:46 (“He shall speak to mankind in the cradle, and in his manhood, and he is of the righteous”), which can be interpreted to refer to the future (“he "), _c('em', [_vm._v("shall")]), _vm._v(" speak”) in terms of Jesus’ ministry yet unfinished."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft124",
      "id": "fn124"
    }
  }, [_vm._v("124")]), _vm._v(" A very important eschatological resource for the Hadith tradition has also been Qur’an 43:61: “And indeed he is a portent of the Hour so do not doubt it but: ‘Follow me. This is a straight path.’ ” Importantly, this passage is in the context of the people of Mecca contesting for the superiority of their gods (or angels?), in response to which the Prophet lifted up the role of Jesus as the sign of the Hour."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft125",
      "id": "fn125"
    }
  }, [_vm._v("125")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("In light of the prominence of Jesus in Muslim eschatology, it is not surprising that the picture of the Antichrist in Islam is not radically different from that in Christianity. Obviously an archenemy of Jesus, the Antichrist can be seen as the personification of evil (similarly to Satan and Iblis). Although the term itself ("), _c('em', [_vm._v("al-Dajjal")]), _vm._v(") does not appear in the Qur’an, there is a wide agreement in Islamic tradition that allusions and indirect references are to be found therein, including the saying attributed to Jesus: “Nay, but verily man is [wont to be] rebellious” (96:6). That sura, even in a quick reading, is easily applied to the Antichrist. The man is “self-sufficient” (v. 7), forbids the servant to pray (vv. 9–10), and closes houses of worship; furthermore, he resists divine revelation, and so forth. Similar types of descriptions of another unknown figure in 68:10–13 and 108:3 have also been seen as allusions to the "), _c('em', [_vm._v("al-Dajjal")]), _vm._v("."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft126",
      "id": "fn126"
    }
  }, [_vm._v("126")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Understandably, the Hadith traditions greatly expand and elaborate on the description and influence of the Antichrist: “The antichrist is short, hen-toed, woolly-haired, one-eyed, an eye-sightless, and neither protruding nor deep-seated. If you are confused about him, know that your Lord is not one-eyed.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft127",
      "id": "fn127"
    }
  }, [_vm._v("127")])]), _vm._v(" Indeed, so seriously is the threat of the Antichrist taken that “[t]here "), _c('a', {
    attrs: {
      "id": "page_58"
    }
  }, [_vm._v("has been no Prophet after Noah who has not warned his people about the antichrist (Dajjal), and I warn you of him.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft128",
      "id": "fn128"
    }
  }, [_vm._v("128")]), _vm._v(" The Antichrist will fight against the unbelievers until the Mahdi and Jesus come and help defeat his power. The intensity and dramatic nature of that battle have been vividly portrayed in some Hadiths."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft129",
      "id": "fn129"
    }
  }, [_vm._v("129")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Along with cosmic and communal eschatology, Muslim theology also speaks widely of the hope for individuals. To the topics of death and resurrection we turn next.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-21"
    }
  }, [_c('em', [_vm._v("Death and Resurrection")])]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_vm._v("For the Muslim, life on this earth is but preparation for eternity,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft130",
      "id": "fn130"
    }
  }, [_vm._v("130")])]), _vm._v(" at the core of which is obedience to and desire to please Allah."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft131",
      "id": "fn131"
    }
  }, [_vm._v("131")])]), _vm._v(" Hence, death should be properly kept in mind (Q 23:15; 3:185)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft132",
      "id": "fn132"
    }
  }, [_vm._v("132")])]), _vm._v(" As in Jewish and Christian traditions, return to earthly life after physical death is impossible (23:109–11)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft133",
      "id": "fn133"
    }
  }, [_vm._v("133")])]), _vm._v(" Not only will everyone die (29:57); there is also given a “fixed” time, a stated life span ("), _c('em', [_vm._v("ajal")]), _vm._v("; Q 6:2). The Qur’an makes clear that physical death forms a final and decisive boundary mark that allows for no return, not even for the repentant deceased."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft134",
      "id": "fn134"
    }
  }, [_vm._v("134")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Although the Qur’an provides preciously few details about what happens between death and resurrection, later traditions have produced fairly detailed accounts."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft135",
      "id": "fn135"
    }
  }, [_vm._v("135")])]), _vm._v(" What Christian theology traditionally has taken as the part of the human person that continues to exist in one way or another after physical death, the soul, is represented in Islamic theology as "), _c('em', [_vm._v("nafs")]), _vm._v(" (soul) or "), _c('em', [_vm._v("ruh")]), _vm._v(" (spirit). Hence, the general idea of death is that while the body decays, “God takes the souls at the time of their death” (Q 39:42). These two terms—“soul” "), _c('a', {
    attrs: {
      "id": "page_59"
    }
  }, [_vm._v("and “spirit”—are not clearly distinguished, and more often than not, they are confused. When a distinction is made, then it is often thought that while “the rational soul ["), _c('em', [_vm._v("nafs")]), _vm._v("], which directs the activities of the body, perishes at physical death … the life-infusing soul or spirit ["), _c('em', [_vm._v("rufs")]), _vm._v("] continues and awaits the coming of the Hour.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft136",
      "id": "fn136"
    }
  }, [_vm._v("136")]), _vm._v(" Thereafter, according to major Muslim tradition, the deceased person meets two angels—named Munkar and Nakir—who test the faith of the person and help determine the final destiny. This is likely the most traditional Islamic view of what happens at death."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft137",
      "id": "fn137"
    }
  }, [_vm._v("137")])]), _vm._v(" In other words, death means the separation of soul from body. Ibn Qayyim clearly supposes the conscious state of the departed soul: “when he begins to move to another world, he sees, hears, and speaks, by some means which, we, the living, do not perceive.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft138",
      "id": "fn138"
    }
  }, [_vm._v("138")])]), _vm._v(" The soul can also be recognized as the departed person by other dead."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft139",
      "id": "fn139"
    }
  }, [_vm._v("139")])]), _vm._v(" In all Muslim accounts of the afterlife, there is thus an intermediate state ("), _c('em', [_vm._v("barzakh")]), _vm._v("), which, according to some Muslim theologians, approaches the Roman Catholic idea of purgatory."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft140",
      "id": "fn140"
    }
  }, [_vm._v("140")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Death, as irrevocable as it is, does not have the last word. Muslims believe in the resurrection of the body."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft141",
      "id": "fn141"
    }
  }, [_vm._v("141")])]), _vm._v(" While the belief in resurrection and (eternal) retribution is firmly taught in the Qur’an (suras 75; 36:77–79; 38:27–28; 41:39; and so forth), Muslim logic also advocates several rational reasons for its necessity and meaningfulness. First, it helps one pursue a good life and avoid evil: even though the human person understands in principle the value of obeying God, one usually does so primarily to avoid punishment and gain benefits. Second, the belief in resurrection and retribution encourages human persons to take care of the poor in the hopes of an eternal reward for all good deeds. Third, the only way for the underprivileged and exploited to hope for justice is to look for resurrection and divine retribution. Fourth, without the hope in resurrection, "), _c('a', {
    attrs: {
      "id": "page_60"
    }
  }, [_vm._v("human life would be miserable because the human person lives longer than other creatures and is capable of suffering and worrying about the future."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft142",
      "id": "fn142"
    }
  }, [_vm._v("142")])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Debates similar to those Jewish and Christian philosophers engage in concerning the nature of the resurrection can be found among Islamic scholars, particularly in medieval times."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft143",
      "id": "fn143"
    }
  }, [_vm._v("143")])]), _vm._v(" Briefly put, the main question is whether resurrection entails a total annihilation of the person before re-creation or a reconstitution and renewal. The main Qur’anic word "), _c('em', [_vm._v("ma’ad")]), _vm._v(" means literally “to return,” but that in itself does not provide a definite hermeneutical key. Indeed, one may find two kinds of directions in scripture itself. Just compare 28:88 (“Everything will perish except His Countenance”), which clearly assumes the annihilationist view, with 10:4: “To Him is the return of all of you.… Truly He originates creation, then recreates it,” which teaches the other option. Generally speaking, the annihilationist view appeals to the Asharites, who in their occasionalism opposed some philosophers’ belief in the eternity of creation: what is totally contingent for its existence is liable to annihilation before the new creation. The Asharites also by and large denied a physical resurrection in favor of a spiritual one in which the “soul” and “spirit” are integrally linked. Others, such as al-Ghazali (who of course was by and large in the Asharite camp) and many Mu‘tazilites, opted for the view according to which both body and soul will be resurrected."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft144",
      "id": "fn144"
    }
  }, [_vm._v("144")])]), _vm._v(" In his “Twentieth Discussion” in "), _c('em', [_vm._v("The Incoherence of Philosophers")]), _vm._v(", al-Ghazali sets out to correct a number of eschatological mistakes, most prominently the denial of bodily resurrection."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft145",
      "id": "fn145"
    }
  }, [_vm._v("145")])]), _vm._v(" His central argument has to do with the “philosophers’ [Avicenna’s] claim that anymore than it is possible for an iron to change into a garment, is the resurrection possible.” Having considered various options for the possibility of one kind of material to be transformed into another (whether instantaneously or over a long period of time), al-Ghazali points to what to him is the main issue of contention in this case—divine agency."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft146",
      "id": "fn146"
    }
  }, [_vm._v("146")])]), _vm._v(" In sum: belief in the resurrection of the body was and is the orthodox belief among the Muslims.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-22"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_61"
    }
  }, [_c('em', [_vm._v("Heaven and Hell—Salvation and Judgment")])])]), _c('a', {
    attrs: {
      "id": "page_61"
    }
  }), _c('p', {
    staticClass: "para1"
  }, [_c('a', {
    attrs: {
      "id": "page_61"
    }
  }, [_vm._v("Consider that a typical list of the basic beliefs of Islam includes “belief in one God, His messengers, His books, His angels, and the day of judgement.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft147",
      "id": "fn147"
    }
  }, [_vm._v("147")]), _vm._v(" Having been created from clay by Allah and appointed to the honorific status of "), _c('em', [_vm._v("khalif")]), _vm._v(", vice-regent, the human being carries responsibility and accountability before Allah. This accounting happens when in the hereafter men and women “return” to their God (Q 32:7–11)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft148",
      "id": "fn148"
    }
  }, [_vm._v("148")])]), _vm._v(" Almost every chapter of the Qur’an speaks of or refers to the theme of judgment."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft149",
      "id": "fn149"
    }
  }, [_vm._v("149")])]), _vm._v(" Similarly to the New Testament, even the evil spirits ("), _c('em', [_vm._v("jinn")]), _vm._v(") will be judged."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft150",
      "id": "fn150"
    }
  }, [_vm._v("150")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Although the Qur’an does not specify the time of the judgment, it assures its readers that the day of judgment is near. The general picture of the day of judgment is very similar to that given in the Bible. Great earthquakes will rock the earth, setting mountains in motion (sura 99). The sky will split open and heaven will be “stripped off,” rolled up like a parchment scroll. The sun will cease to shine; the stars will be scattered and fall upon the earth. The oceans will boil over. Graves will be opened, the earth bringing forth its burdens—hidden sins, lost stories, and the dead (sura 82). People will seek vainly to flee from the divine wrath. All will bow, willingly or not, before God. In traditional Islamic thought, the day of judgment is a period of great cosmic conflict when the forces of Satan—represented by a false messiah ("), _c('em', [_vm._v("al-Dajjal")]), _vm._v(") and Gog and Magog—combat the forces of God, led by the Mahdi and Jesus."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft151",
      "id": "fn151"
    }
  }, [_vm._v("151")])]), _vm._v(" With judgment constantly in view, life on this earth for the Muslim is preparation for eternity."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft152",
      "id": "fn152"
    }
  }, [_vm._v("152")])]), _vm._v(" After resurrection, each human person is given a “book” that indicates the final destiny (18:49), either heaven or hell."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft153",
      "id": "fn153"
    }
  }, [_vm._v("153")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The normal way in Islam to be saved (and to avoid the fires of hell) is to believe in the unity of God ("), _c('em', [_vm._v("shahada")]), _vm._v(") and pursue good deeds (Q 262);"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft154",
      "id": "fn154"
    }
  }, [_vm._v("154")])]), _vm._v(" one should also avoid grave sins. What is debated among the Muslim schools is indeed the lot of the (gravely) sinning believer. Wide disagreements among the "), _c('a', {
    attrs: {
      "id": "page_62"
    }
  }, [_vm._v("(medieval) schools could not be reconciled."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft155",
      "id": "fn155"
    }
  }, [_vm._v("155")]), _vm._v(" That debate relates to another: How do the person’s good and bad deeds account for the final judgment received? Notwithstanding unanimity, common to all opinions is the centrality of obedience to Allah or lack thereof; furthermore, it was widely agreed that only grave sins bring about judgment."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft156",
      "id": "fn156"
    }
  }, [_vm._v("156")])]), _vm._v(" On the one hand, for some (particularly among the Mu‘tazilites and the conservative Kharijites [Khawārijites]), any merit accrued by good deeds can in principle be canceled out by grave sins, especially "), _c('em', [_vm._v("shirk")]), _vm._v(" (associating God with anything non-God) and "), _c('em', [_vm._v("kufr")]), _vm._v(" (unbelief, reluctance to submit under God’s authority). On the other hand, Asharites and other major Sunni traditions believe in the balancing of good and evil deeds to determine the final outcome. This view is based particularly on Q 99:7–8: “So whoever does an atom’s weight of good shall see it, and whoever does an atom’s weight of evil shall see it.” Rather than a semi-automatic process in which one grave sin deletes the value of all good deeds, it is left to Allah to balance the scales. Furthermore, human repentance and intercession count as mitigating factors in this majority (Asharite) position."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft157",
      "id": "fn157"
    }
  }, [_vm._v("157")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("What about non-Muslims? It seems like the Qur’an teaches a fairly inclusive view of salvation: “Surely those who believe, and those of Jewry, and the Christians, and the Sabaeans, whoever believes in God and the Last Day, and performs righteous deeds—their wage is with their Lord, and no fear shall befall them, neither shall they grieve” (2:62). The implications of this passage are of course widely debated among historical and contemporary Muslim scholars. Echoing the biblical view for those who have never heard the gospel, the Qur’an teaches that “we do not punish unless We send a messenger [who explains to people their obligations]” (17:15). Ultimately, the matter is to be left open."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft158",
      "id": "fn158"
    }
  }, [_vm._v("158")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("What is not debated is that there are two destinies as taught in the scripture (9:100–102; 7:37–51;"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft159",
      "id": "fn159"
    }
  }, [_vm._v("159")])]), _vm._v(" 75:20–25; and so forth); tradition also affirms it univocally."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft160",
      "id": "fn160"
    }
  }, [_vm._v("160")])]), _vm._v(" Although understandably the Islamic tradition does not speak "), _c('a', {
    attrs: {
      "id": "page_63"
    }
  }, [_vm._v("with one voice on the nature and existence of hell, there is a general understanding:"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft161",
      "id": "fn161"
    }
  }, [_vm._v("161")]), _vm._v(" it is a place of great pain and torture, as graphically described both in the Qur’an and in later tradition,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft162",
      "id": "fn162"
    }
  }, [_vm._v("162")])]), _vm._v(" particularly among the Mu‘tazilites."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft163",
      "id": "fn163"
    }
  }, [_vm._v("163")])]), _vm._v(" Their views of eternal destinies were significantly guided by a semitechnical understanding of God’s justice: “God, having declared Himself to be just (Qur’an 6:115; 16:90; 21:47; 57:25) … was constrained always to judge exactly according to the just deserts of each soul at the judgement, so that there would be no escape for the impenitent sinner.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft164",
      "id": "fn164"
    }
  }, [_vm._v("164")])]), _vm._v(" Yet human freedom and responsibility were not ruled out; ultimately the final destiny is in the hands of the human person, as “God only creates in humans the power or ability to act, not the acts themselves.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft165",
      "id": "fn165"
    }
  }, [_vm._v("165")])]), _vm._v(" Out of that insistence emerged the Mu‘tazilite conception of “promise and threat,” that is, the determination of both the blessed and the damned state by the individual himself or herself (in keeping with God’s justice and fairness). The major Sunni tradition (particularly Hanbilites and Asharites) resisted this kind of notion of “security of salvation” and left the question totally up to Allah."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft166",
      "id": "fn166"
    }
  }, [_vm._v("166")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Unlike the major view, the Mu‘tazilite position thus resists the power of intercession, the idea of which has in some way or another been part of the Muslim spiritual texture from the beginning; indeed, the possibility and conditions of intercession are widely discussed in the Hadith. One such famous debate has to do with whether intercession continues to be effective even on the day of resurrection. Asserting to a group of Muslim scholars that he himself will be the leader of humankind that day, the Prophet teaches that neither Adam nor Abraham nor Moses has the prerogative to intercede; only he himself can."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft167",
      "id": "fn167"
    }
  }, [_vm._v("167")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("What about heaven?"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft168",
      "id": "fn168"
    }
  }, [_vm._v("168")])]), _vm._v(" Often depicted in the Qur’an with garden images (3:190–99; 22:19–20; sura 37), paradise is a place of great enjoyment, peace, and reunion. The Qur’an offers sensual descriptions, including the pleasures "), _c('a', {
    attrs: {
      "id": "page_64"
    }
  }, [_vm._v("of exquisitely delicious food and drink, as well as sexual relations with divine maidens (often interpreted metaphorically)."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft169",
      "id": "fn169"
    }
  }, [_vm._v("169")]), _vm._v(" Similarly to the Bible, there are also various levels of rewards for the blessed ones, the details of which have occupied Islamic exegetes for hundreds of years."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft170",
      "id": "fn170"
    }
  }, [_vm._v("170")])]), _vm._v(" Particularly splendid accounts of paradise (“Garden”) can be found in the Hadith, such as its having eight gates and no less than one hundred levels, the distance between each of them being as long as that between heaven and earth."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft171",
      "id": "fn171"
    }
  }, [_vm._v("171")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Similarly to Christian tradition, Muslim scholars have debated widely about the possibility of the “beatific vision.” For the Muslim tradition, that question is even more contested because of Islam’s uttermost insistence on the transcendence of God (without in any way undermining Islam’s surprisingly robust account of immanence). The question also has to do with the possibility and conditions of the (bodily) resurrected person’s “ ‘ocular vision’ of God who is the ultimate spiritual reality.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft172",
      "id": "fn172"
    }
  }, [_vm._v("172")])]), _vm._v(" Not surprisingly, opinions vary. Whereas the Asharites (in keeping with their disgust for the highly abstract Mu‘tazilite-type speculations) basically refrain from making up their minds by not delving too deeply into the details of the problem, some others, among the Shi’ites particularly, are convinced that such a vision is possible."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft173",
      "id": "fn173"
    }
  }, [_vm._v("173")])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec1",
    attrs: {
      "id": "sec-11"
    }
  }, [_c('strong', [_vm._v("“End-Time” Visions and Symbols in Hindu Traditions")])]), _vm._v(" "), _c('h1', {
    staticClass: "sec2-t0",
    attrs: {
      "id": "sec2-23"
    }
  }, [_c('em', [_vm._v("In Search of a Distinctive Hindu Vision of the “End”")])]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_vm._v("Unlike Abrahamic traditions, “Hinduism has no last day or end time, nor any completion of history, resurrection of the dead, and universal last judgment.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft174",
      "id": "fn174"
    }
  }, [_vm._v("174")])]), _vm._v(" The focus lies rather on “the deliverance of the individual from the unreal realm of the empirical and temporal to the timeless realm of the "), _c('a', {
    attrs: {
      "id": "page_65"
    }
  }, [_vm._v("spirit.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft175",
      "id": "fn175"
    }
  }, [_vm._v("175")]), _vm._v(" Because of its endless-cycle worldview, Hindu eschatology can be best called an eternalistic and organic process,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft176",
      "id": "fn176"
    }
  }, [_vm._v("176")])]), _vm._v(" as opposed to the linear and historical perspective in the Abrahamic faiths. Although the Lord is at times called the Creator (particularly in the Bhagavad-Gita),"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft177",
      "id": "fn177"
    }
  }, [_vm._v("177")])]), _vm._v(" there is neither a doctrine of creation nor a Creator in any sense similar to those of Abrahamic traditions; this corresponds to the lack of any (final) end."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft178",
      "id": "fn178"
    }
  }, [_vm._v("178")])]), _vm._v(" Importantly, the role of the deities is ambiguous when it comes to end times. A number of deities undergo death and even rebirth in another form."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft179",
      "id": "fn179"
    }
  }, [_vm._v("179")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("“Cosmic eschatology” is usually described in terms of "), _c('em', [_vm._v("kalpas")]), _vm._v(" (a somewhat later development)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft180",
      "id": "fn180"
    }
  }, [_vm._v("180")])]), _vm._v(" Each "), _c('em', [_vm._v("kalpa")]), _vm._v(" encompasses the life span from origination to dissolution. It equals one day in the life of Brahma (the first of the three main deities). Each "), _c('em', [_vm._v("kalpa")]), _vm._v(" is further divided into four ages with different religious-moral tones, from full "), _c('em', [_vm._v("dharma")]), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft181",
      "id": "fn181"
    }
  }, [_vm._v("181")])]), _vm._v(" (righteousness) to "), _c('em', [_vm._v("adharma")]), _vm._v(", the worst possible condition. At the end of a "), _c('em', [_vm._v("kalpa")]), _vm._v(", a great dissolution occurs, “which coincides with the end of the life of Brahma. The world will be reabsorbed into Brahma by involution and remain in that state until the hatching of a new cosmic age.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft182",
      "id": "fn182"
    }
  }, [_vm._v("182")])]), _vm._v(" And so "), _c('em', [_vm._v("ad finitum")]), _vm._v(". Of interest to Christian theology is that when the lack of "), _c('em', [_vm._v("dharma")]), _vm._v(" is at its worst, the "), _c('em', [_vm._v("avatar")]), _vm._v(" (incarnation) of Vishnu comes down, the beloved Krishna (or any other of the incarnations of Vishnu or even Shiva)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft183",
      "id": "fn183"
    }
  }, [_vm._v("183")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Are there any millennial elements in various Hindu traditions? It is widely agreed that only with the rise of anticolonialist movements in the nineteenth century did millennial groups emerge. At the same time, various types of self-made gurus appeared, and in their activities millennial features "), _c('a', {
    attrs: {
      "id": "page_66"
    }
  }, [_vm._v("were present. “That being said, however, the Hindu traditions do have visions of cataclysmic change, eschatological destruction, and a radical remaking of the world that have certain structural parallels to Western visions of the Millennium.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft184",
      "id": "fn184"
    }
  }, [_vm._v("184")])]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-24"
    }
  }, [_c('em', [_vm._v("Death, Rebirth, and Karmic Samsara")])]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_vm._v("The last two books (ninth and tenth) of the oldest Vedic scripture, Rig Veda, speak extensively of death."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft185",
      "id": "fn185"
    }
  }, [_vm._v("185")])]), _vm._v(" The Funeral Hymn (10.14), dedicated to Yama, the god of death—the first one to die and show the rest of mortals the way to go"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft186",
      "id": "fn186"
    }
  }, [_vm._v("186")])]), _vm._v("—speaks of death in terms of meeting Yama and the “Fathers” (the honored ancestors)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft187",
      "id": "fn187"
    }
  }, [_vm._v("187")])]), _vm._v(" Yama is assisted by two messenger dogs, the guardians of death’s pathway. Yama and the “Fathers” prepare a wonderful place for the deceased, “a place to rest in adorned with days and beams of light and water” (10.14.8–11[9])."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft188",
      "id": "fn188"
    }
  }, [_vm._v("188")])]), _vm._v(" The Upanishads significantly expand the theology of death. They contain the well-known legend of the Naciketas (or Nakiketas) in Katha Upanishad (1.1–3), whose main message is the possibility of two destinies, either that of pleasure and worldly enjoyment, which is nothing more than illusion, or the “good way,” that of true knowledge."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft189",
      "id": "fn189"
    }
  }, [_vm._v("189")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The most innovative and theologically significant Upanishadic development has to do with the evolvement of the doctrines of karma and transmigration of the soul (rebirth)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft190",
      "id": "fn190"
    }
  }, [_vm._v("190")])]), _vm._v(" The soul continues its afterlife journey from one state to another conditioned by the deeds of one’s lifetime."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft191",
      "id": "fn191"
    }
  }, [_vm._v("191")])]), _vm._v(" Until one is ready to be absorbed into “Reality” ("), _c('em', [_vm._v("satya")]), _vm._v("), the migration continues."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft192",
      "id": "fn192"
    }
  }, [_vm._v("192")])]), _vm._v(" Known "), _c('a', {
    attrs: {
      "id": "page_67"
    }
  }, [_vm._v("as karmic samsara, continuous rebirths are believed by all Hindu movements, both theistic and otherwise."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft193",
      "id": "fn193"
    }
  }, [_vm._v("193")]), _vm._v(" It is essential to note that samsara is a “universal” law concerning “the conditioned and ever changing universe as contrasted to an unconditioned, eternal, and transcendent state ("), _c('em', [_vm._v("moksa")]), _vm._v(" or "), _c('em', [_vm._v("nirvāna")]), _vm._v(").” The liberated one is no longer under karma."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft194",
      "id": "fn194"
    }
  }, [_vm._v("194")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Deriving from the ritual context, karma evolves from moral and immoral actions from the past lives of the individual to their consequences and one’s actions in subsequent lives, finally extending to the notion that “good and bad karma may also be transferred from one person to another under certain circumstances.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft195",
      "id": "fn195"
    }
  }, [_vm._v("195")])]), _vm._v(" At death, “the various component parts of … [the deceased person’s] body unite with [his] corresponding counterparts in nature, while the sum total of his karma remains attached to his self ("), _c('em', [_vm._v("atman")]), _vm._v("). The force of this karma decides the nature of his next birth where he reaps the fruit of what he merits.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft196",
      "id": "fn196"
    }
  }, [_vm._v("196")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("That teaching helps put death in a different perspective: as aversive as it may appear to human desire to cling to life,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft197",
      "id": "fn197"
    }
  }, [_vm._v("197")])]), _vm._v(" it is not the ultimate reality and has only relative significance. Death is an “earthly” matter and hence belongs to the “appearance” part of reality."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft198",
      "id": "fn198"
    }
  }, [_vm._v("198")])]), _vm._v(" “The knowing (Self) is not born, it dies "), _c('a', {
    attrs: {
      "id": "page_68"
    }
  }, [_vm._v("not; it sprang from nothing, nothing sprang from it. The Ancient is unborn, eternal, everlasting; he is not killed, though the body is killed.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft199",
      "id": "fn199"
    }
  }, [_vm._v("199")]), _vm._v(" That said, however, reverence for the dead and the obligation of the relatives of the deceased are an integral part of Hindu cultures,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft200",
      "id": "fn200"
    }
  }, [_vm._v("200")])]), _vm._v(" and sophisticated funeral rites have evolved to help start the journey in the afterlife.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Bhagavad-Gita, the “Bible of the common folks,” affirms the basic Upanishadic teachings: the inevitability of death, its transitory nature because of transmigration, and two destinies (at least as long as one has not yet achieved the ultimate goal)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft201",
      "id": "fn201"
    }
  }, [_vm._v("201")])]), _vm._v(" As is well known, the Gita pays special attention to one’s last thoughts, that is, whether one is totally devoted to one’s deity or to some earthly goal: “The One who leaves the body, at the hour of death, remembering Me [Krishna, an "), _c('em', [_vm._v("avatara")]), _vm._v(" of Vishnu] attains My abode.… By contemplating on Me with an unwavering mind, disciplined by the practice of meditation, one attains the Supreme divine spirit.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft202",
      "id": "fn202"
    }
  }, [_vm._v("202")])]), _vm._v(" Unlike in philosophical schools (such as "), _c('em', [_vm._v("advaita")]), _vm._v(" of Sankara) in which true knowledge is the key to release, in all "), _c('em', [_vm._v("bhakti")]), _vm._v(" (devotional) schools love for the deity is at the center. In those spiritualities, “[e]very man becomes what he worships.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft203",
      "id": "fn203"
    }
  }, [_vm._v("203")])]), _vm._v(" The discussion of the theistic (Vaishnavistic), particularly devotional forms of Hinduism helps us also to understand that the workings of karma can be understood less mechanistically (as in "), _c('em', [_vm._v("advaita")]), _vm._v(") and more “as a soterial path leading to acceptance of the grace of Krishna.” Furthermore, differently from Sankara’s view, according to which there is “one non-dual self which, through the mysterious power of maya comes to see itself as many individual selves,” Ramanuja’s Vaishnava version “is that the material body is a reflection of a spiritual body, so that each soul has a distinct personality in the spiritual world, and liberation consists in being freed from the limitations of ‘false ego,’ and relating in loving devotion to the supreme personality of Godhead.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft204",
      "id": "fn204"
    }
  }, [_vm._v("204")])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-25"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_69"
    }
  }, [_vm._v("Avidya "), _c('em', [_vm._v("and")]), _vm._v(" Moksa"), _c('em', [_vm._v(": “Ignorance” and “Salvation”")])])]), _c('a', {
    attrs: {
      "id": "page_69"
    }
  }), _c('p', {
    staticClass: "para1"
  }, [_c('a', {
    attrs: {
      "id": "page_69"
    }
  }, [_vm._v("Similarly to the doctrine of resurrection, rebirth raises the question of the constitution of human nature"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft205",
      "id": "fn205"
    }
  }, [_vm._v("205")]), _vm._v(" and the corollary question of what continues beyond physical death. Although the Vedanta Hindu philosophical schools do not typically lean toward the Hellenistic type of body-soul dualism (or if they do, they frame it differently), there is a dualism of “true” (real) and “not-self.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft206",
      "id": "fn206"
    }
  }, [_vm._v("206")])]), _vm._v(" As long as one does not grasp the single most important insight that “Self ["), _c('em', [_vm._v("atman")]), _vm._v("] is indeed Brahman [the eternal Self],”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft207",
      "id": "fn207"
    }
  }, [_vm._v("207")])]), _vm._v(" one is distanced from the real self."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft208",
      "id": "fn208"
    }
  }, [_vm._v("208")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Hinduism knows nothing like the Christian doctrine of sin and fall. The underlying Hindu diagnosis of what human “bondage” is has to do with "), _c('em', [_vm._v("avidya")]), _vm._v(", “ignorance.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft209",
      "id": "fn209"
    }
  }, [_vm._v("209")])]), _vm._v(" It makes one cling to "), _c('em', [_vm._v("maya")]), _vm._v(", “fiction,” and thus be subject to the effects of karma, leading to rebirths over and over again."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft210",
      "id": "fn210"
    }
  }, [_vm._v("210")])]), _vm._v(" Only the removal of this bondage, "), _c('em', [_vm._v("moksa")]), _vm._v(", makes release or deliverance possible."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft211",
      "id": "fn211"
    }
  }, [_vm._v("211")])]), _vm._v(" The key is the enlightenment insight into the true nature of reality."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft212",
      "id": "fn212"
    }
  }, [_vm._v("212")])]), _vm._v(" It is safe to say that by and large Hindu movements “aim at the practical end of salvation. The systems mean by release (moksa) the recovery by the soul of its natural integrity, from which sin and error drive it.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft213",
      "id": "fn213"
    }
  }, [_vm._v("213")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("This karmic samsara template conceived of the nature of heaven and hell differently. Only vaguely intuited in the Vedic literature, a fairly clearly defined picture of heaven and hell does not appear until the great epics, particularly the "), _c('em', [_vm._v("Mahabharata")]), _vm._v("."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft214",
      "id": "fn214"
    }
  }, [_vm._v("214")])]), _vm._v(" Because of rebirth, their meaning is obviously different from those of Abrahamic traditions. With bad karma, one is reborn into hell "), _c('a', {
    attrs: {
      "id": "page_70"
    }
  }, [_vm._v("for penalty and purification; with good karma, for a longer period of time of enjoyment in heaven. Neither one, however, is the ultimate destiny."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft215",
      "id": "fn215"
    }
  }, [_vm._v("215")]), _vm._v(" The last book of the great epic, titled “The Book of Climbing to Heaven” ("), _c('em', [_vm._v("Svargarohanika Parva")]), _vm._v("), provides detailed descriptions of both."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft216",
      "id": "fn216"
    }
  }, [_vm._v("216")])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec1",
    attrs: {
      "id": "sec-12"
    }
  }, [_c('strong', [_vm._v("Buddhist Visions of End and “Release”")])]), _vm._v(" "), _c('h1', {
    staticClass: "sec2-t0",
    attrs: {
      "id": "sec2-26"
    }
  }, [_c('em', [_vm._v("In Search of an Eschatological Vision after Buddhist Traditions")])]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_vm._v("Although Buddhism, similarly to Hinduism, does not know any final closure—and therefore we should speak of “relative” eschatology"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft217",
      "id": "fn217"
    }
  }, [_vm._v("217")])]), _vm._v("—already during the time of Gautama himself a diversity of views of the “end” of human life had emerged."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft218",
      "id": "fn218"
    }
  }, [_vm._v("218")])]), _vm._v(" As is well known, Buddha showed great reticence toward speculations into the metaphysical questions of the end (and origin) because he took this to be “unbeneficial” in the pursuit of final release."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft219",
      "id": "fn219"
    }
  }, [_vm._v("219")])]), _vm._v(" When entering into speculations concerning the “end,” somewhat similarly to Hindu tradition, Buddhism speaks of the “history” of the cosmos in terms of exceedingly long ages—called “great eons” ("), _c('em', [_vm._v("mahākalpa")]), _vm._v(")—and divides them into four periods, beginning with the destruction of the cosmos and extending to various durations of renovation when the universe again reemerges. Within each period a number of subperiods can be discerned in which the quality of human life and the level of morality vary."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft220",
      "id": "fn220"
    }
  }, [_vm._v("220")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Rather than a doctrine of creation,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft221",
      "id": "fn221"
    }
  }, [_vm._v("221")])]), _vm._v(" Buddha taught the principle of “dependent origination,” also called “causal interdependence.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft222",
      "id": "fn222"
    }
  }, [_vm._v("222")])]), _vm._v(" Not surprisingly, "), _c('a', {
    attrs: {
      "id": "page_71"
    }
  }, [_vm._v("then, “[e]verything formed is in a constant process of change.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft223",
      "id": "fn223"
    }
  }, [_vm._v("223")]), _vm._v(" Everything is fleeting and in a process"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft224",
      "id": "fn224"
    }
  }, [_vm._v("224")])]), _vm._v("—everything but nirvana (which is not “conditioned”)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft225",
      "id": "fn225"
    }
  }, [_vm._v("225")])]), _vm._v(" The conditioned nature of all of reality is part of and makes sense against the most distinctive Buddhist concept, namely, the "), _c('em', [_vm._v("dukkha")]), _vm._v("."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft226",
      "id": "fn226"
    }
  }, [_vm._v("226")])]), _vm._v(" Rather than using “suffering” (or “pain” or “stress”) for "), _c('em', [_vm._v("dukkha")]), _vm._v(", it is best to leave the term without English translation to avoid misunderstanding. It is intentionally an ambiguous word related to the nonstatic nature of reality. All Buddhist schools consider "), _c('em', [_vm._v("dukkha")]), _vm._v(" to be the main challenge in life and, consequently, extinction of "), _c('em', [_vm._v("dukkha")]), _vm._v(" to be the main goal, the "), _c('em', [_vm._v("summa")]), _vm._v(" of everything in Buddhism and its scriptures."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft227",
      "id": "fn227"
    }
  }, [_vm._v("227")])]), _vm._v(" Suffering is inescapable as long as one is in the circle of life and death, samsara. To be more precise, it is the craving (the second “Noble Truth”) that is its real root and cause."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft228",
      "id": "fn228"
    }
  }, [_vm._v("228")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Keeping in mind these foundational assumptions about the nature of the cosmos and life, we are prepared to begin to investigate the distinctive nature of the Buddhist analysis of how to achieve release from the “mass of suffering” and endless cycle of samsara. As is well known, Buddhism joins Indian traditions in affirming the doctrine of rebirth. That said, because of its distinctive analysis of human nature, known as "), _c('em', [_vm._v("anatta")]), _vm._v(", “no-self,” that doctrine is affirmed in a radically different manner from Hinduism. To that investigation we devote the bulk of this section on Buddhist eschatology.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-27"
    }
  }, [_c('em', [_vm._v("“No-Self,”")]), _vm._v(" Kamma, "), _c('em', [_vm._v("and Rebirth")])]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_vm._v("Buddhism’s most distinctive doctrine is the denial of a constitutive permanent self, persisting personhood."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft229",
      "id": "fn229"
    }
  }, [_vm._v("229")])]), _vm._v(" This is but a result of the codependent origination "), _c('a', {
    attrs: {
      "id": "page_72"
    }
  }, [_vm._v("and impermanence as well as the "), _c('em', [_vm._v("dukkha")]), _vm._v(" nature of all conditioned phenomena. Calling the person a “self” is just an elusive, conventional way of referring to that fleeting combination of elements."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft230",
      "id": "fn230"
    }
  }, [_vm._v("230")]), _vm._v(" Therefore also, “[t]here is no doer of a deed.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft231",
      "id": "fn231"
    }
  }, [_vm._v("231")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("To be liberated from the illusion of being permanent and hence clinging to anything conditioned, the “salvific” insight into the true nature of reality and being is needed (release from samsara, the cycle of rebirths). Otherwise, the continuous process of rebirth continues due to "), _c('em', [_vm._v("kamma")]), _vm._v(", which in the case of those who “engage in bad actions … generate[s] unwholesome kamma that leads them to rebirth into lower states of existence,” including pain and suffering."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft232",
      "id": "fn232"
    }
  }, [_vm._v("232")])]), _vm._v(" Lest one conceive of the karmic samsara cycle of rebirths along the lines of Hinduism—and common sense—that is, that the deceased self will be reborn, the doctrine of "), _c('em', [_vm._v("anatta")]), _vm._v(" should be remembered: “Rebirth, in the Buddhist conception, is not the transmigration of a self or soul but the continuation of a process, a flux of becoming in which successive lives are linked together by causal transmission of influence rather than by substantial identity.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft233",
      "id": "fn233"
    }
  }, [_vm._v("233")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("What, then, is the Buddhist final release, "), _c('em', [_vm._v("nibbana?")]), _vm._v(" What is its nature? Who will achieve it? Because the Buddha, as mentioned, was not interested in metaphysical speculations but rather in “putting an end to suffering and stress,”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft234",
      "id": "fn234"
    }
  }, [_vm._v("234")])]), _vm._v(" he did not encourage speculation; rather, he set out to teach the path to liberation; doing so, he began to sketch some idea of its nature.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-28"
    }
  }, [_c('em', [_vm._v("Nirvana and the Cessation of “Desire”")])]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_vm._v("The goal of the Buddhist pursuit is to achieve the enlightening insight into the nature of the "), _c('em', [_vm._v("dukkha")]), _vm._v(" in order to overcome the desire to cling to it."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft235",
      "id": "fn235"
    }
  }, [_vm._v("235")])]), _vm._v(" Behind the (misplaced) craving is ignorance. No wonder the very first step in the "), _c('a', {
    attrs: {
      "id": "page_73"
    }
  }, [_vm._v("Eightfold Path of skillful means is rightful knowledge."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft236",
      "id": "fn236"
    }
  }, [_vm._v("236")]), _vm._v(" Even Buddha is not the Savior. One may only “take refuge” in Buddha. He is the one who shows the possibility of attaining such a releasing knowledge."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft237",
      "id": "fn237"
    }
  }, [_vm._v("237")])]), _vm._v(" Ultimately, “one is one’s own refuge, who else could be the refuge?”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft238",
      "id": "fn238"
    }
  }, [_vm._v("238")])]), _vm._v(" The road to enlightenment is long and tedious."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft239",
      "id": "fn239"
    }
  }, [_vm._v("239")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("What, if anything, then can be said of the nature of "), _c('em', [_vm._v("nibbana")]), _vm._v("? Recall that this is the only nonconditioned aspect of reality and therefore free from change and decay."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft240",
      "id": "fn240"
    }
  }, [_vm._v("240")])]), _vm._v(" In "), _c('em', [_vm._v("nibbana")]), _vm._v(", there is “neither dimension of the infinitude of space, nor dimension of the infinitude of consciousness, nor dimension of nothingness, nor dimension of neither perception nor non-perception; neither this world, nor the next world.… This, just this, is the end of stress.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft241",
      "id": "fn241"
    }
  }, [_vm._v("241")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The main logical challenge to such a vision is well known among both Buddhists and its critics: If everything is nonpermanent, how can nirvana then be the “final” goal? I am not aware of satisfactory solutions."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft242",
      "id": "fn242"
    }
  }, [_vm._v("242")])]), _vm._v(" How helpful is the statement in "), _c('em', [_vm._v("Lankavatar Sutra")]), _vm._v(" (2.1.7): “Thou dost not vanish in Nirvana, nor is Nirvana abiding in thee; for it transcends the duality of knowing and known and of being and non-being”?"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft243",
      "id": "fn243"
    }
  }, [_vm._v("243")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The end result of reaching "), _c('em', [_vm._v("nibbana")]), _vm._v(" in the Theravada tradition is the "), _c('em', [_vm._v("arahant")]), _vm._v(", derived from the “worthy” (also rendered as “accomplished”). That "), _c('a', {
    attrs: {
      "id": "page_74"
    }
  }, [_vm._v("enlightened one is the person, “with taints destroyed, who has lived the holy life, done what had to be done, laid down the burden, reached the true goal, destroyed the fetters of being, and is completely liberated through final knowledge.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft244",
      "id": "fn244"
    }
  }, [_vm._v("244")]), _vm._v(" At the time of the emergence of the Mahayana tradition, this original concept of "), _c('em', [_vm._v("arahant")]), _vm._v(" was revised into Boddhisattva, the Enlightened One who for the sake of others postpones the stepping into "), _c('em', [_vm._v("nibbana")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Is this nihilism? To merely respond to this important question in an obvious way—that it is not because there is no “self” in Buddhism"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft245",
      "id": "fn245"
    }
  }, [_vm._v("245")])]), _vm._v("—misses the point and avoids the question. True, there is no “self” in that system, but there is something, how elusively and ambiguously one may wish to express it, that at least has an afterlife, if not eternally, then at least for a long period of time.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Having now outlined in some detail the visions of the “end” and “consummation” in four living faith traditions—following the inquiry into the resources of natural sciences—we will begin to develop a distinctively Christian eschatology. In the course of that process, other faith traditions as well as the sciences’ insights will be critically evaluated.")]), _vm._v(" "), _c('p', {
    staticClass: "image"
  }, [_c('img', {
    attrs: {
      "alt": "image",
      "src": "images/foot.jpg"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn1",
      "id": "ft1"
    }
  }, [_vm._v("1")]), _vm._v(". See Hick, "), _c('em', [_vm._v("Death and Eternal Life")]), _vm._v(", p. 21.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn2",
      "id": "ft2"
    }
  }, [_vm._v("2")]), _vm._v(". Badham and Badham, “Death and Immortality,” p. 1.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn3",
      "id": "ft3"
    }
  }, [_vm._v("3")]), _vm._v(". Werblowsky, “Eschatology,” pp. 2833–34.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn4",
      "id": "ft4"
    }
  }, [_vm._v("4")]), _vm._v(". J. Walls, introduction to "), _c('em', [_vm._v("OHE")]), _vm._v(", p. 4. The now-classic study is Eliade, "), _c('em', [_vm._v("The Myth of the Eternal Return")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn5",
      "id": "ft5"
    }
  }, [_vm._v("5")]), _vm._v(". Nattier, “Buddhist Eschatology,” p. 151.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn6",
      "id": "ft6"
    }
  }, [_vm._v("6")]), _vm._v(". Werblowsky, “Eschatology,” p. 2834.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn7",
      "id": "ft7"
    }
  }, [_vm._v("7")]), _vm._v(". For thoughtful constructive reflections, see R. Boyd, “The End of Eschatology?” and “In the End.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn8",
      "id": "ft8"
    }
  }, [_vm._v("8")]), _vm._v(". Badham and Badham, “Death and Immortality,” p. 6; similarly, Hick ("), _c('em', [_vm._v("Death and Eternal Life")]), _vm._v(", pp. 29–34) seeks to discuss “the idea of a global theology of death.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn9",
      "id": "ft9"
    }
  }, [_vm._v("9")]), _vm._v(". Segal, “Judaism,” p. 13: “The authors of the Hebrew Bible, apart from its latest strata, did not teach that individuals survive death in any religiously significant way.” Still a reliable guide is Gowan, "), _c('em', [_vm._v("Eschatology in the Old Testament")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn10",
      "id": "ft10"
    }
  }, [_vm._v("10")]), _vm._v(". Novak, “Jewish Eschatology,” p. 118; as an example, he mentions "), _c('em', [_vm._v("b. Sanhedrin")]), _vm._v(" 92b and 97b.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn11",
      "id": "ft11"
    }
  }, [_vm._v("11")]), _vm._v(". See Goldberg, “Bound Up in the Bond of Life,” p. 99.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn12",
      "id": "ft12"
    }
  }, [_vm._v("12")]), _vm._v(". See Arnold, “Old Testament Eschatology,” p. 25.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn13",
      "id": "ft13"
    }
  }, [_vm._v("13")]), _vm._v(". A “good” death was one that happened in mature age (Gen. 25:8; Job 42:17), whereas death at an early age was depicted as divine judgment (1 Sam. 2:32); Schwarz, "), _c('em', [_vm._v("Eschatology")]), _vm._v(", p. 37.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn14",
      "id": "ft14"
    }
  }, [_vm._v("14")]), _vm._v(". For a detailed study, see Brueggemann, "), _c('em', [_vm._v("Theology of the Old Testament")]), _vm._v(", pp. 233–50. In light of its this-worldly orientation, the idea of a “good death” (as in Gen. 25:8; Deut. 34:7; and so forth) is understandable; however, that should not hinder our seeing that by and large the Old Testament sees death in deeply negative terms; see Gillman, "), _c('em', [_vm._v("The Death of Death")]), _vm._v(", pp. 69–72, 79–81.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn15",
      "id": "ft15"
    }
  }, [_vm._v("15")]), _vm._v(". Schwarz, "), _c('em', [_vm._v("Eschatology")]), _vm._v(", pp. 35–38 (35–36). As a result, no ethical idea was yet involved with death and afterlife. Cohn-Sherbok, “Death and Immortality,” p. 24; more widely, Kohler, "), _c('em', [_vm._v("Jewish Theology")]), _vm._v(", p. 279.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn16",
      "id": "ft16"
    }
  }, [_vm._v("16")]), _vm._v(". For a standard reliable source, see Morenz, "), _c('em', [_vm._v("Egyptian Religion")]), _vm._v(", chap. 9; see also Schwarz, "), _c('em', [_vm._v("Eschatology")]), _vm._v(", pp. 32–35; Sumegi, "), _c('em', [_vm._v("Understanding Death")]), _vm._v(", pp. 51–61.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn17",
      "id": "ft17"
    }
  }, [_vm._v("17")]), _vm._v(". See chaps. 2 and 3 in Gillman, "), _c('em', [_vm._v("The Death of Death")]), _vm._v("; another useful, shorter treatment is Sumegi, "), _c('em', [_vm._v("Understanding Death")]), _vm._v(", pp. 80–85.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn18",
      "id": "ft18"
    }
  }, [_vm._v("18")]), _vm._v(". Brueggemann, "), _c('em', [_vm._v("Theology of the Old Testament")]), _vm._v(", pp. 483–84.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn19",
      "id": "ft19"
    }
  }, [_vm._v("19")]), _vm._v(". Schwarz, "), _c('em', [_vm._v("Eschatology")]), _vm._v(", pp. 39–40; Gillman, "), _c('em', [_vm._v("The Death of Death")]), _vm._v(", chap. 4.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn20",
      "id": "ft20"
    }
  }, [_vm._v("20")]), _vm._v(". See further Sumegi, "), _c('em', [_vm._v("Understanding Death")]), _vm._v(", pp. 86–90. Segal (“Judaism,” p. 18) rightly reminds us that even with Daniel, it is not clear if bodily survival or spiritual immortality is in view.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn21",
      "id": "ft21"
    }
  }, [_vm._v("21")]), _vm._v(". Novak, “Jewish Eschatology,” p. 115. The standard source is Nickelsburg, "), _c('em', [_vm._v("Resurrection, Immortality, and Eternal Life in Intertestamental Judaism")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn22",
      "id": "ft22"
    }
  }, [_vm._v("22")]), _vm._v(". Novak, “Jewish Eschatology,” p. 114. Segal (“Judaism,” p. 20) reminds us that rabbinic theology hardly is systematic in nature, nor uniform.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn23",
      "id": "ft23"
    }
  }, [_vm._v("23")]), _vm._v(". Gillman, "), _c('em', [_vm._v("The Death of Death")]), _vm._v(", p. 12 (attributing the idea to Gerson Cohen, personal conversation).")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn24",
      "id": "ft24"
    }
  }, [_vm._v("24")]), _vm._v(". Novak, “Jewish Eschatology,” p. 115; materially similarly, Charles, "), _c('em', [_vm._v("A Critical History of the Doctrine of a Future Life in Israel")]), _vm._v(", p. 157.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn25",
      "id": "ft25"
    }
  }, [_vm._v("25")]), _vm._v(". Gillman, "), _c('em', [_vm._v("The Death of Death")]), _vm._v(", p. 18. For a massive study, see Avery-Peck and Neusner, eds., "), _c('em', [_vm._v("Death, Afterlife, and the World-to-Come")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn26",
      "id": "ft26"
    }
  }, [_vm._v("26")]), _vm._v(". Novak, “Jewish Eschatology,” p. 114.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn27",
      "id": "ft27"
    }
  }, [_vm._v("27")]), _vm._v(". See further Arnold, “Old Testament Eschatology,” pp. 24–25 (24). For a highly nuanced negotiation between this world and the world to come, see the Mishnaic tractate "), _c('em', [_vm._v("m. Pirkei Avot")]), _vm._v(" (“Ethics of the Fathers”), chap. 4; for comments, see Novak, “Jewish Eschatology,” p. 116.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn28",
      "id": "ft28"
    }
  }, [_vm._v("28")]), _vm._v(". Gillman, "), _c('em', [_vm._v("The Death of Death")]), _vm._v(", p. 22.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn29",
      "id": "ft29"
    }
  }, [_vm._v("29")]), _vm._v(". For a highly useful discussion of the sources and background, see J. Collins, “Apocalyptic Eschatology in the Ancient World,” pp. 40–55. For a guide to apocalyptic literature, see J. Collins, "), _c('em', [_vm._v("The Apocalyptic Imagination")]), _vm._v(". For an almost encyclopedic collection of essays on all things apocalyptic, see Amanat and Bernhardsson, eds., "), _c('em', [_vm._v("Imagining the End")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn30",
      "id": "ft30"
    }
  }, [_vm._v("30")]), _vm._v(". See Tabor, “Ancient Jewish and Early Christian Millennialism,” pp. 252–53.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn31",
      "id": "ft31"
    }
  }, [_vm._v("31")]), _vm._v(". For details, see Schwarz, "), _c('em', [_vm._v("Eschatology")]), _vm._v(", pp. 48–49.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn32",
      "id": "ft32"
    }
  }, [_vm._v("32")]), _vm._v(". Schwarz, "), _c('em', [_vm._v("Eschatology")]), _vm._v(", pp. 51–55 (53).")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn33",
      "id": "ft33"
    }
  }, [_vm._v("33")]), _vm._v(". Gillman, "), _c('em', [_vm._v("The Death of Death")]), _vm._v(", p. 173; chap. 7 is devoted totally to this and related mystical eschatologies.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn34",
      "id": "ft34"
    }
  }, [_vm._v("34")]), _vm._v(". See Segal, “Judaism,” pp. 25–26.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn35",
      "id": "ft35"
    }
  }, [_vm._v("35")]), _vm._v(". See Ripsman, "), _c('em', [_vm._v("Reincarnation in Jewish Mysticism and Gnosticism")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn36",
      "id": "ft36"
    }
  }, [_vm._v("36")]), _vm._v(". The standard Kabbalah source is "), _c('em', [_vm._v("Sefer haBahir")]), _vm._v(", “The Book of Illumination.” On the views of Kabbalah’s most well-known representative, see the English translation of (and short introduction to) "), _c('em', [_vm._v("Shaar HaGilgulim")]), _vm._v(", “Gates of Incarnation.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn37",
      "id": "ft37"
    }
  }, [_vm._v("37")]), _vm._v(". "), _c('em', [_vm._v("b. Shabbat")]), _vm._v(" 30b (for the background to the story, see first folio 30a).")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn38",
      "id": "ft38"
    }
  }, [_vm._v("38")]), _vm._v(". "), _c('em', [_vm._v("b. Berakot")]), _vm._v(" 34b; cited in Novak, “Jewish Eschatology,” p. 128 (but in a different translation).")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn39",
      "id": "ft39"
    }
  }, [_vm._v("39")]), _vm._v(". See "), _c('em', [_vm._v("b. Berakot")]), _vm._v(" 17a and comments by Novak, “Jewish Eschatology,” p. 118 (who cites it from another translation from my reference).")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn40",
      "id": "ft40"
    }
  }, [_vm._v("40")]), _vm._v(". For details and sources, see Cohn-Sherbok, “Death and Immortality,” pp. 26–28.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn41",
      "id": "ft41"
    }
  }, [_vm._v("41")]), _vm._v(". "), _c('em', [_vm._v("b. Eiruvin")]), _vm._v(" 19a ("), _c('a', {
    attrs: {
      "href": "http://halakhah.com/pdf/moed/Eiruvin.pdf",
      "target": "_blank"
    }
  }, [_vm._v("http://halakhah.com/pdf/moed/Eiruvin.pdf")]), _vm._v(") for a description, see Cohn-Sherbok, “Death and Immortality,” pp. 28–31.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn42",
      "id": "ft42"
    }
  }, [_vm._v("42")]), _vm._v(". For details, see my "), _c('em', [_vm._v("Creation and Humanity")]), _vm._v(", pp. 413–15.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn43",
      "id": "ft43"
    }
  }, [_vm._v("43")]), _vm._v(". K. Ward, "), _c('em', [_vm._v("Religion and Human Nature")]), _vm._v(", p. 175.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn44",
      "id": "ft44"
    }
  }, [_vm._v("44")]), _vm._v(". Jacobs, "), _c('em', [_vm._v("Jewish Theology")]), _vm._v(", p. 245. This is not to deny that there is a deep woundedness and potential for drastic evil in humanity, but rather to understand evil in a way that does not make it innate or compromise human freedom; see pp. 246–47; Cohon, "), _c('em', [_vm._v("Essays in Jewish Theology")]), _vm._v(", p. 225.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn45",
      "id": "ft45"
    }
  }, [_vm._v("45")]), _vm._v(". Cohn-Sherbok, “Death and Immortality,” p. 26; Segal, “Judaism,” pp. 21–22.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn46",
      "id": "ft46"
    }
  }, [_vm._v("46")]), _vm._v(". Cohn-Sherbok, “Death and Immortality,” p. 26. A highly regarded list was drafted by Maimonides, "), _c('em', [_vm._v("Mishneh Torah Book of Knowledge")]), _vm._v(", bk. 1: "), _c('em', [_vm._v("Sefer Maddah: Hilchot Teshuvah")]), _vm._v(" (“Laws of Repentance”), chap. 3.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn47",
      "id": "ft47"
    }
  }, [_vm._v("47")]), _vm._v(". Novak, “Jewish Eschatology,” p. 117.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn48",
      "id": "ft48"
    }
  }, [_vm._v("48")]), _vm._v(". For details and historical sources, see Novak, “Jewish Eschatology,” pp. 121–22.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn49",
      "id": "ft49"
    }
  }, [_vm._v("49")]), _vm._v(". The defining starting point for such consideration is the famous debate in the first century "), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("C.E.")]), _vm._v(" between Rabbis Eliezer and Joshua ben Hananiah (or Jehoshua); see tractate "), _c('em', [_vm._v("Sanhedrin")]), _vm._v(" 10:2 (p. 122). For details, see Novak, “Jewish Eschatology,” pp. 120–21; Gillman, "), _c('em', [_vm._v("The Death of Death")]), _vm._v(", pp. 133–34.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn50",
      "id": "ft50"
    }
  }, [_vm._v("50")]), _vm._v(". Schwarz, "), _c('em', [_vm._v("Eschatology")]), _vm._v(", pp. 45–47.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn51",
      "id": "ft51"
    }
  }, [_vm._v("51")]), _vm._v(". See further Cohn-Sherbok, “Death and Immortality,” p. 31.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn52",
      "id": "ft52"
    }
  }, [_vm._v("52")]), _vm._v(". For representative opinions from leading rabbis, see Cohn-Sherbok, “Death and Immortality,” pp. 32–33.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn53",
      "id": "ft53"
    }
  }, [_vm._v("53")]), _vm._v(". Gillman, "), _c('em', [_vm._v("The Death of Death")]), _vm._v(", chap. 5, titled “The Canonization of a Doctrine.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn54",
      "id": "ft54"
    }
  }, [_vm._v("54")]), _vm._v(". Novak, “Jewish Eschatology,” pp. 122–23; Gillman, "), _c('em', [_vm._v("The Death of Death")]), _vm._v(", pp. 122–25.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn55",
      "id": "ft55"
    }
  }, [_vm._v("55")]), _vm._v(". "), _c('em', [_vm._v("m. Sanhedrin")]), _vm._v(" 10:1: “But the following have no portion in the World to Come: He who says that resurrection is not a Torah doctrine, the Torah is not from Heaven …”")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn56",
      "id": "ft56"
    }
  }, [_vm._v("56")]), _vm._v(". Novak, “Jewish Eschatology,” p. 123.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn57",
      "id": "ft57"
    }
  }, [_vm._v("57")]), _vm._v(". Maimonides, "), _c('em', [_vm._v("Shloshah Asar Ikkarim")]), _vm._v(" (the “Thirteen Fundamental Principles”), #13 (resurrection); ##8–9 (Torah). In his later years, Maimonides penned the masterful "), _c('em', [_vm._v("Treatise on the Resurrection")]), _vm._v(", in which he had to defend his orthodoxy against charges of heresy; see Gillman, "), _c('em', [_vm._v("The Death of Death")]), _vm._v(", chap. 6. The standard collection of essays is edited by Dienstag, "), _c('em', [_vm._v("Eschatology in Maimonidean Thought")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn58",
      "id": "ft58"
    }
  }, [_vm._v("58")]), _vm._v(". For a detailed scrutiny of sources indicating both of these beliefs, see Gillman, "), _c('em', [_vm._v("The Death of Death")]), _vm._v(", pp. 115–22 particularly. See also Segal, “Judaism,” pp. 23–25.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn59",
      "id": "ft59"
    }
  }, [_vm._v("59")]), _vm._v(". While a marginal belief, there is the postrabbinical idea of the divinity of the soul that makes it immortal. See Scholem, "), _c('em', [_vm._v("Major Trends in Jewish Mysticism")]), _vm._v(", pp. 239–43.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn60",
      "id": "ft60"
    }
  }, [_vm._v("60")]), _vm._v(". Materially so also Novak, “Jewish Eschatology,” p. 123 (without mentioning Thomism); see also pp. 75–78, 105–12; Stern, “Afterlife,” p. 152.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn61",
      "id": "ft61"
    }
  }, [_vm._v("61")]), _vm._v(". As in the above-mentioned "), _c('em', [_vm._v("Amidah")]), _vm._v(" (#2 in the Eighteen Benedictions) prayer: “You, O Lord, are mighty forever, you revive the dead … and keep faith with those who sleep in the dust”; "), _c('a', {
    attrs: {
      "href": "http://www.egrc.net/articles/other/amidah.html",
      "target": "_blank"
    }
  }, [_vm._v("http://www.egrc.net/articles/other/amidah.html")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn62",
      "id": "ft62"
    }
  }, [_vm._v("62")]), _vm._v(". "), _c('em', [_vm._v("b. Kethubot")]), _vm._v(" 111b.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn63",
      "id": "ft63"
    }
  }, [_vm._v("63")]), _vm._v(". "), _c('em', [_vm._v("b. Kethubot")]), _vm._v(" 111b.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn64",
      "id": "ft64"
    }
  }, [_vm._v("64")]), _vm._v(". Segal, “Judaism,” p. 27; see also Adelman, “Jews, Eschatology, and Contemporary Visions.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn65",
      "id": "ft65"
    }
  }, [_vm._v("65")]), _vm._v(". Novak, “Jewish Eschatology,” p. 126.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn66",
      "id": "ft66"
    }
  }, [_vm._v("66")]), _vm._v(". For a detailed discussion, see chap. 2 in Raphael, "), _c('em', [_vm._v("Jewish Views of the Afterlife")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn67",
      "id": "ft67"
    }
  }, [_vm._v("67")]), _vm._v(". For an overview, see Ariel, “Radical Millennial Movements,” pp. 1–15. For millenarian Zionism, consult Aran, “From Religious Zionism to Zionist Religion”; for Christian fundamentalist connections, see Ariel, “Born Again in a Land of Paradox.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn68",
      "id": "ft68"
    }
  }, [_vm._v("68")]), _vm._v(". The English translation is titled "), _c('em', [_vm._v("Phaedon; Or the Death of Socrates")]), _vm._v(" (1789). An introduction and English translation can be found at "), _c('a', {
    attrs: {
      "href": "http://www.schillerinstitute.org/transl/mend_phadn_cullen.html",
      "target": "_blank"
    }
  }, [_vm._v("http://www.schillerinstitute.org/transl/mend_phadn_cullen.html")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn69",
      "id": "ft69"
    }
  }, [_vm._v("69")]), _vm._v(". Gillman, "), _c('em', [_vm._v("The Death of Death")]), _vm._v(", pp. 191–92 (192).")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn70",
      "id": "ft70"
    }
  }, [_vm._v("70")]), _vm._v(". Gillman, "), _c('em', [_vm._v("The Death of Death")]), _vm._v(", pp. 196–204 (quotation p. 202, from the Philadelphia Statement of 1869). Similar conclusions were reached by Kohler, "), _c('em', [_vm._v("Jewish Theology")]), _vm._v(", pp. 296–97.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn71",
      "id": "ft71"
    }
  }, [_vm._v("71")]), _vm._v(". Gillman, "), _c('em', [_vm._v("The Death of Death")]), _vm._v(", pp. 205–8.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn72",
      "id": "ft72"
    }
  }, [_vm._v("72")]), _vm._v(". Gillman, "), _c('em', [_vm._v("The Death of Death")]), _vm._v(", pp. 208–11 (210).")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn73",
      "id": "ft73"
    }
  }, [_vm._v("73")]), _vm._v(". Gillman, "), _c('em', [_vm._v("The Death of Death")]), _vm._v(", pp. 212–13.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn74",
      "id": "ft74"
    }
  }, [_vm._v("74")]), _vm._v(". Gillman, "), _c('em', [_vm._v("The Death of Death")]), _vm._v(", p. 213, emphasis in original.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn75",
      "id": "ft75"
    }
  }, [_vm._v("75")]), _vm._v(". Gillman, "), _c('em', [_vm._v("The Death of Death")]), _vm._v(", pp. 213–14.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn76",
      "id": "ft76"
    }
  }, [_vm._v("76")]), _vm._v(". Herberg, "), _c('em', [_vm._v("Judaism and Modern Man")]), _vm._v(", pp. 231–32; I am indebted to Gillman, "), _c('em', [_vm._v("The Death of Death")]), _vm._v(", pp. 220–25.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn77",
      "id": "ft77"
    }
  }, [_vm._v("77")]), _vm._v(". A. A. Cohen, “Resurrection of the Dead,” p. 812; for a detailed discussion, see Gillman, "), _c('em', [_vm._v("The Death of Death")]), _vm._v(", pp. 225–30.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn78",
      "id": "ft78"
    }
  }, [_vm._v("78")]), _vm._v(". A. A. Cohen, “Resurrection of the Dead,” p. 808; see also p. 811.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn79",
      "id": "ft79"
    }
  }, [_vm._v("79")]), _vm._v(". Borowitz, "), _c('em', [_vm._v("Reform Judaism Today")]), _vm._v(", p. 48, cited in Gillman, "), _c('em', [_vm._v("The Death of Death")]), _vm._v(", p. 232; see also pp. 230–36. Interestingly, Borowitz’s "), _c('em', [_vm._v("Renewing the Covenant")]), _vm._v(" has precious little to say of afterlife and eschatology; it merely makes a few scattered references to immortality and resurrection, has nothing to say of hell, and is almost silent about heaven.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn80",
      "id": "ft80"
    }
  }, [_vm._v("80")]), _vm._v(". Gillman, "), _c('em', [_vm._v("The Death of Death")]), _vm._v(", pp. 243–74 (260, 265 respectively).")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn81",
      "id": "ft81"
    }
  }, [_vm._v("81")]), _vm._v(". For an earlier essay, see J. Taylor, “Some Aspects of Islamic Eschatology.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn82",
      "id": "ft82"
    }
  }, [_vm._v("82")]), _vm._v(". Blankinship, “The Early Creed,” p. 34.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn83",
      "id": "ft83"
    }
  }, [_vm._v("83")]), _vm._v(". For the Qur’anic rebuttal of the Meccan community’s disbelief in resurrection and judgment, see, e.g., 22:1–7. For others, see 17:49, 98; 19:66; 37:16–17; 50:3; and 75:3. See J. Smith and Haddad, "), _c('em', [_vm._v("The Islamic Understanding of Death and Resurrection")]), _vm._v(", p. 1 n. 1.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn84",
      "id": "ft84"
    }
  }, [_vm._v("84")]), _vm._v(". Chittick, “Muslim Eschatology,” p. 132. It is claimed that nearly a third of the Qur’an is eschatological in nature; Siddiqui, “Death, Resurrection, and Human Destiny,” p. 30. Useful surveys are Rahman, "), _c('em', [_vm._v("Major Themes of the Qur’ān")]), _vm._v(", chap. 6; Haleem, “Life and Beyond in the Qur’an,” pp. 66–79.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn85",
      "id": "ft85"
    }
  }, [_vm._v("85")]), _vm._v(". See Renard, ed., "), _c('em', [_vm._v("Islamic Theological Themes")]), _vm._v(", p. 13.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn86",
      "id": "ft86"
    }
  }, [_vm._v("86")]), _vm._v(". See further J. Smith and Haddad, "), _c('em', [_vm._v("The Islamic Understanding of Death and Resurrection")]), _vm._v(", pp. vii–ix, xi–xii. For the radical break from the views and rites of death of pre-Islamic times (times of “ignorance”), see Kassis, “Islam,” pp. 49–50. I have also benefited from Bijlefeld, “Eschatology.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn87",
      "id": "ft87"
    }
  }, [_vm._v("87")]), _vm._v(". Translated by Watt, in Renard, ed., "), _c('em', [_vm._v("Islamic Theological Themes")]), _vm._v(", pp. 104–9.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn88",
      "id": "ft88"
    }
  }, [_vm._v("88")]), _vm._v(". Hermansen, “Eschatology,” p. 309.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn89",
      "id": "ft89"
    }
  }, [_vm._v("89")]), _vm._v(". See Hermansen, “Eschatology,” p. 310.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn90",
      "id": "ft90"
    }
  }, [_vm._v("90")]), _vm._v(". J. Smith and Haddad, "), _c('em', [_vm._v("The Islamic Understanding of Death and Resurrection")]), _vm._v(", p. 2.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn91",
      "id": "ft91"
    }
  }, [_vm._v("91")]), _vm._v(". For this conception and its main features, see Chittick, “Muslim Eschatology,” p. 133.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn92",
      "id": "ft92"
    }
  }, [_vm._v("92")]), _vm._v(". J. Smith and Haddad, "), _c('em', [_vm._v("The Islamic Understanding of Death and Resurrection")]), _vm._v(", pp. 3–5 (3).")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn93",
      "id": "ft93"
    }
  }, [_vm._v("93")]), _vm._v(". For a basic guide, see Cook, “Early Islamic and Classical Sunni and Shi’ite Apocalyptic Movements”; more widely in Cook, "), _c('em', [_vm._v("Studies in Muslim Apocalyptic")]), _vm._v(". See also Bashear, “Muslim Apocalypses.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn94",
      "id": "ft94"
    }
  }, [_vm._v("94")]), _vm._v(". For a basic guide, see Kenney, “Millennialism and Radical Islamist Movements.” David Cook offers a useful survey of the literature in "), _c('em', [_vm._v("Contemporary Muslim Apocalyptic Literature")]), _vm._v(". See also Devji, "), _c('em', [_vm._v("Landscapes of the Jihad")]), _vm._v("; G. Fuller, "), _c('em', [_vm._v("The Future of Political Islam")]), _vm._v("; Moussalli, "), _c('em', [_vm._v("Radical Islamic Fundamentalism")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn95",
      "id": "ft95"
    }
  }, [_vm._v("95")]), _vm._v(". “Wang Daiyu on Translating "), _c('em', [_vm._v("Tawhīd")]), _vm._v(" in Chinese Traditional Terms.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn96",
      "id": "ft96"
    }
  }, [_vm._v("96")]), _vm._v(". For details about the “hour,” see Stieglecker, "), _c('em', [_vm._v("Die Glaubenslehren des Islam")]), _vm._v(", ##1371–72, pp. 747–48.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn97",
      "id": "ft97"
    }
  }, [_vm._v("97")]), _vm._v(". Q 7:187; 33:63; 79:42. See also "), _c('em', [_vm._v("Sahir Bukhari")]), _vm._v(", bk. 3 (“Knowledge”), #56.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn98",
      "id": "ft98"
    }
  }, [_vm._v("98")]), _vm._v(". So also Q 6:59; 31:34. This paragraph is based on Saritoprak, "), _c('em', [_vm._v("Islam’s Jesus")]), _vm._v(", pp. 38–40.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn99",
      "id": "ft99"
    }
  }, [_vm._v("99")]), _vm._v(". See further Saritoprak, "), _c('em', [_vm._v("Islam’s Jesus")]), _vm._v(", pp. 41–42.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn100",
      "id": "ft100"
    }
  }, [_vm._v("100")]), _vm._v(". As listed (and translated) in Saritoprak, "), _c('em', [_vm._v("Islam’s Jesus")]), _vm._v(", p. 48 n. 21.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn101",
      "id": "ft101"
    }
  }, [_vm._v("101")]), _vm._v(". See D. Peterson, “Eschatology.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn102",
      "id": "ft102"
    }
  }, [_vm._v("102")]), _vm._v(". For representative examples, see Alam, “Signs of Hour”; “The 72 Signs of Doomsday (Qayamat).”")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn103",
      "id": "ft103"
    }
  }, [_vm._v("103")]), _vm._v(". "), _c('em', [_vm._v("Abu-Dawud")]), _vm._v(", bk. 30, ##4228–37.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn104",
      "id": "ft104"
    }
  }, [_vm._v("104")]), _vm._v(". "), _c('em', [_vm._v("Sahih Muslim")]), _vm._v(", bk. 41, ##6881–990.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn105",
      "id": "ft105"
    }
  }, [_vm._v("105")]), _vm._v(". For details, see Stieglecker, "), _c('em', [_vm._v("Die Glaubenslehren des Islam")]), _vm._v(", ##1363–66, pp. 742–44; see also Q 27:82–84 and comments in Stieglecker, #1368, pp. 745–46.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn106",
      "id": "ft106"
    }
  }, [_vm._v("106")]), _vm._v(". Saritoprak, "), _c('em', [_vm._v("Islam’s Jesus")]), _vm._v(", pp. 58–59.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn107",
      "id": "ft107"
    }
  }, [_vm._v("107")]), _vm._v(". See Saritoprak, "), _c('em', [_vm._v("Islam’s Jesus")]), _vm._v(", chaps. 7 and 8.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn108",
      "id": "ft108"
    }
  }, [_vm._v("108")]), _vm._v(". Saritoprak, "), _c('em', [_vm._v("Islam’s Jesus")]), _vm._v(", pp. 47–48.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn109",
      "id": "ft109"
    }
  }, [_vm._v("109")]), _vm._v(". A highly nuanced and insightful discussion of Muslim “messianism” and Mahdi’s place therein is Riffat Hassan, “Messianism and Islam.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn110",
      "id": "ft110"
    }
  }, [_vm._v("110")]), _vm._v(". Stieglecker, "), _c('em', [_vm._v("Die Glaubenslehren des Islam")]), _vm._v(", #1362, p. 742; Saritoprak, "), _c('em', [_vm._v("Islam’s Jesus")]), _vm._v(", p. 85.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn111",
      "id": "ft111"
    }
  }, [_vm._v("111")]), _vm._v(". See the important book by Amini, "), _c('em', [_vm._v("Al-Imam al-Mahdi")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn112",
      "id": "ft112"
    }
  }, [_vm._v("112")]), _vm._v(". The term "), _c('em', [_vm._v("muhtadun")]), _vm._v(", “the guided ones,” appears in Q 3:51–56; 3:90; 6:82; and 4:175. This is taken by some as reference to the Mahdi.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn113",
      "id": "ft113"
    }
  }, [_vm._v("113")]), _vm._v(". Sachedina, "), _c('em', [_vm._v("Islamic Messianism")]), _vm._v(". In terms of differences from the mainline Sunni tradition, among Shi’ites there are also groups that believe that Ali or the Prophet himself may return; see further Saritoprak, "), _c('em', [_vm._v("Islam’s Jesus")]), _vm._v(", pp. 91–94.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn114",
      "id": "ft114"
    }
  }, [_vm._v("114")]), _vm._v(". Godzliher, "), _c('em', [_vm._v("Introduction to Islamic Theology and Law")]), _vm._v(", pp. 197–98; see also Saritoprak, "), _c('em', [_vm._v("Islam’s Jesus")]), _vm._v(", chap. 6; Blichfeldt, "), _c('em', [_vm._v("Early Mahdism")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn115",
      "id": "ft115"
    }
  }, [_vm._v("115")]), _vm._v(". For other historical figures, see Hermansen, “Eschatology,” p. 315.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn116",
      "id": "ft116"
    }
  }, [_vm._v("116")]), _vm._v(". Ibn Khaldun, "), _c('em', [_vm._v("Muqaddimah")]), _vm._v(", 2:156, cited in Saritoprak, "), _c('em', [_vm._v("Islam’s Jesus")]), _vm._v(", pp. 88–89.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn117",
      "id": "ft117"
    }
  }, [_vm._v("117")]), _vm._v(". Saritoprak, "), _c('em', [_vm._v("Islam’s Jesus")]), _vm._v(", pp. 85, 87.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn118",
      "id": "ft118"
    }
  }, [_vm._v("118")]), _vm._v(". Saritoprak, "), _c('em', [_vm._v("Islam’s Jesus")]), _vm._v(", p. xii.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn119",
      "id": "ft119"
    }
  }, [_vm._v("119")]), _vm._v(". There are, however, important Hadith commentaries on passages such as Q 43:61 that mention Jesus; for details, see Stieglecker, "), _c('em', [_vm._v("Die Glaubenslehren des Islam")]), _vm._v(", #1367, p. 745.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn120",
      "id": "ft120"
    }
  }, [_vm._v("120")]), _vm._v(". Saritoprak, "), _c('em', [_vm._v("Islam’s Jesus")]), _vm._v(", pp. 22–23.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn121",
      "id": "ft121"
    }
  }, [_vm._v("121")]), _vm._v(". Saritoprak, "), _c('em', [_vm._v("Islam’s Jesus")]), _vm._v(", p. xv; chap. 4.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn122",
      "id": "ft122"
    }
  }, [_vm._v("122")]), _vm._v(". Q 4:156–58; 3:55; see my "), _c('em', [_vm._v("Christ and Reconciliation")]), _vm._v(", pp. 388–93.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn123",
      "id": "ft123"
    }
  }, [_vm._v("123")]), _vm._v(". See Stieglecker, "), _c('em', [_vm._v("Die Glaubenslehren des Islam")]), _vm._v(", #1367, p. 745, my translation.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn124",
      "id": "ft124"
    }
  }, [_vm._v("124")]), _vm._v(". This view was advocated by such influential medieval interpreters as Muhammad Al-Jurjani; see Saritoprak, "), _c('em', [_vm._v("Islam’s Jesus")]), _vm._v(", p. 23 n. 1. A similar interpretation can be offered of Q 19:28; on the sophisticated exegetical-hermeneutical devices used by another leading medieval commentator, Fakh al-Din al-Razi, to insure the future reference, see pp. 24–25.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn125",
      "id": "ft125"
    }
  }, [_vm._v("125")]), _vm._v(". For details, see Saritoprak, "), _c('em', [_vm._v("Islam’s Jesus")]), _vm._v(", pp. 27–28.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn126",
      "id": "ft126"
    }
  }, [_vm._v("126")]), _vm._v(". Stieglecker, "), _c('em', [_vm._v("Die Glaubenslehren des Islam")]), _vm._v(", ##1358–60, pp. 740–41; Saritoprak, "), _c('em', [_vm._v("Islam’s Jesus")]), _vm._v(", pp. 94–95; McGinn, "), _c('em', [_vm._v("Antichrist")]), _vm._v(", p. 111.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn127",
      "id": "ft127"
    }
  }, [_vm._v("127")]), _vm._v(". "), _c('em', [_vm._v("Abu Dawud")]), _vm._v(", bk. 37 (“Battles”), #4306; the same book also speaks frequently of the Antichrist in war situations.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn128",
      "id": "ft128"
    }
  }, [_vm._v("128")]), _vm._v(". "), _c('em', [_vm._v("Abu Dawud")]), _vm._v(", bk. 40 (“Tributes, Spoil and Rulership”), #4738.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn129",
      "id": "ft129"
    }
  }, [_vm._v("129")]), _vm._v(". See further Saritoprak, "), _c('em', [_vm._v("Islam’s Jesus")]), _vm._v(", p. 97.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn130",
      "id": "ft130"
    }
  }, [_vm._v("130")]), _vm._v(". Tug, “Death and Immortality in Islamic Thought,” p. 88; see also the important statement in al-Ghazali, "), _c('em', [_vm._v("Alchemy of Happiness")]), _vm._v(", p. 74.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn131",
      "id": "ft131"
    }
  }, [_vm._v("131")]), _vm._v(". Kassis, “Islam,” pp. 50–51.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn132",
      "id": "ft132"
    }
  }, [_vm._v("132")]), _vm._v(". Afsaruddin, “Death, Resurrection, and Human Destiny,” pp. 43–44. See the listing “Selected Passages from al-Ghazālī’s "), _c('em', [_vm._v("The Remembrance of Death and the Afterlife")]), _vm._v(".” For comments, see Winter, “Al-Ghazālī’ on Death,” pp. 161–65.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn133",
      "id": "ft133"
    }
  }, [_vm._v("133")]), _vm._v(". See Q 23:98–101; J. Smith and Haddad, "), _c('em', [_vm._v("The Islamic Understanding of Death and Resurrection")]), _vm._v(", pp. 7–8; Siddiqui, “Death, Resurrection, and Human Destiny,” pp. 25–26.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn134",
      "id": "ft134"
    }
  }, [_vm._v("134")]), _vm._v(". For comments, see Sumegi, "), _c('em', [_vm._v("Understanding Death")]), _vm._v(", p. 147.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn135",
      "id": "ft135"
    }
  }, [_vm._v("135")]), _vm._v(". See further J. Smith and Haddad, "), _c('em', [_vm._v("The Islamic Understanding of Death and Resurrection")]), _vm._v(", pp. 32–33.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn136",
      "id": "ft136"
    }
  }, [_vm._v("136")]), _vm._v(". J. Smith and Haddad, "), _c('em', [_vm._v("The Islamic Understanding of Death and Resurrection")]), _vm._v(", pp. 19–20 (20); see also Sumegi, "), _c('em', [_vm._v("Understanding Death")]), _vm._v(", pp. 143–44.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn137",
      "id": "ft137"
    }
  }, [_vm._v("137")]), _vm._v(". For a succinct account, see Stieglecker, "), _c('em', [_vm._v("Die Glaubenslehren des Islam")]), _vm._v(", #1341, pp. 730–31; on Munkar and Nakir, see #1344, pp. 732–33; for details about what happens between the grave and resurrections, see ##1351–54, pp. 736–38. For a slightly different account, see “The Soul’s Journey after Death,” introduction (pp. 2–3).")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn138",
      "id": "ft138"
    }
  }, [_vm._v("138")]), _vm._v(". “The Soul’s Journey after Death,” p. 2.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn139",
      "id": "ft139"
    }
  }, [_vm._v("139")]), _vm._v(". “The Soul’s Journey after Death,” p. 3.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn140",
      "id": "ft140"
    }
  }, [_vm._v("140")]), _vm._v(". See further Afsaruddin, “Death, Resurrection, and Human Destiny,” p. 46. Described by many, the journey of the soul in the afterlife is most vividly accounted in the seventeenth-century master Mulla Sadra’s "), _c('em', [_vm._v("Four Journeys")]), _vm._v(", one of the many volumes devoted to end times.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn141",
      "id": "ft141"
    }
  }, [_vm._v("141")]), _vm._v(". Stieglecker, "), _c('em', [_vm._v("Die Glaubenslehren des Islam")]), _vm._v(", #1383, p. 755, my translation. For the importance of that doctrine and useful documentation, see Mahmud, "), _c('em', [_vm._v("The Creed of Islam")]), _vm._v(", chap. 5.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn142",
      "id": "ft142"
    }
  }, [_vm._v("142")]), _vm._v(". Stieglecker, "), _c('em', [_vm._v("Die Glaubenslehren des Islam")]), _vm._v(", ##1383–87, pp. 755–57.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn143",
      "id": "ft143"
    }
  }, [_vm._v("143")]), _vm._v(". Differently from some Jewish traditions, reincarnation (metempsychosis) has never received approval among any significant Muslim movements. It is noteworthy, however, that Mulla Sadra of the seventeenth century saw it important to offer sustained critique of it in his "), _c('em', [_vm._v("Four Journeys")]), _vm._v(". For details, see Rizvi, “Mulla Sadra.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn144",
      "id": "ft144"
    }
  }, [_vm._v("144")]), _vm._v(". For details, see Stieglecker, "), _c('em', [_vm._v("Die Glaubenslehren des Islam")]), _vm._v(", ##1388–96, pp. 757–61.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn145",
      "id": "ft145"
    }
  }, [_vm._v("145")]), _vm._v(". He also corrects mistakes about annihilationism and resurrection in his "), _c('em', [_vm._v("The Remembrance of Death and the Afterlife")]), _vm._v(", pp. 121–25.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn146",
      "id": "ft146"
    }
  }, [_vm._v("146")]), _vm._v(". Al-Ghazali, "), _c('em', [_vm._v("Tahafut Al-Falasifah")]), _vm._v(", pp. 220–22; citations on #48, p. 222; help to locate the reference came from Griffel, “Al-Ghazali.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn147",
      "id": "ft147"
    }
  }, [_vm._v("147")]), _vm._v(". Haleem, “Qur’an and Hadith,” p. 25; for details, see pp. 29–30.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn148",
      "id": "ft148"
    }
  }, [_vm._v("148")]), _vm._v(". See further J. Smith and Haddad, "), _c('em', [_vm._v("The Islamic Understanding of Death and Resurrection")]), _vm._v(", pp. 11–14.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn149",
      "id": "ft149"
    }
  }, [_vm._v("149")]), _vm._v(". See further Fromherz, “Judgment, Final”; Nyang, “The Teaching of the Quran,” pp. 77–80.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn150",
      "id": "ft150"
    }
  }, [_vm._v("150")]), _vm._v(". For details, see Nyang, “The Teaching of the Quran,” pp. 81–82.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn151",
      "id": "ft151"
    }
  }, [_vm._v("151")]), _vm._v(". D. Peterson, “Eschatology.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn152",
      "id": "ft152"
    }
  }, [_vm._v("152")]), _vm._v(". See Schirrmacher, “They Are Not All Martyrs,” p. 251.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn153",
      "id": "ft153"
    }
  }, [_vm._v("153")]), _vm._v(". For angels’ role as “bookkeepers,” see Nyang, “The Teaching of the Quran,” p. 81.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn154",
      "id": "ft154"
    }
  }, [_vm._v("154")]), _vm._v(". For details of Islamic soteriology, see chap. 8 in my "), _c('em', [_vm._v("Spirit and Salvation")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn155",
      "id": "ft155"
    }
  }, [_vm._v("155")]), _vm._v(". J. Smith and Haddad, "), _c('em', [_vm._v("The Islamic Understanding of Death and Resurrection")]), _vm._v(", p. 24.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn156",
      "id": "ft156"
    }
  }, [_vm._v("156")]), _vm._v(". Q 4:31: “If you avoid the grave sins that are forbidden you, We will absolve you of your evil deeds and admit you by an honourable gate.” The problem is, of course, what constitutes a “grave” sin.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn157",
      "id": "ft157"
    }
  }, [_vm._v("157")]), _vm._v(". J. Smith and Haddad, "), _c('em', [_vm._v("The Islamic Understanding of Death and Resurrection")]), _vm._v(", pp. 22–24.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn158",
      "id": "ft158"
    }
  }, [_vm._v("158")]), _vm._v(". Saritoprak, "), _c('em', [_vm._v("Islam’s Jesus")]), _vm._v(", pp. 51–53 (52).")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn159",
      "id": "ft159"
    }
  }, [_vm._v("159")]), _vm._v(". Here there are three groups; the third one consists of those still waiting for the judgment and hoping to enter heaven.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn160",
      "id": "ft160"
    }
  }, [_vm._v("160")]), _vm._v(". See Haleem, “Commentary on Selected Qur’ānic Texts,” pp. 148–49.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn161",
      "id": "ft161"
    }
  }, [_vm._v("161")]), _vm._v(". See further Afsaruddin, “Death, Resurrection, and Human Destiny,” pp. 48–50.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn162",
      "id": "ft162"
    }
  }, [_vm._v("162")]), _vm._v(". Chittick, “Muslim Eschatology,” p. 134.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn163",
      "id": "ft163"
    }
  }, [_vm._v("163")]), _vm._v(". See further the eleventh-century Abd al-Jabbar’s “Five Principles,” pp. 212–14. See also Blankinship, “The Early Creed,” p. 47.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn164",
      "id": "ft164"
    }
  }, [_vm._v("164")]), _vm._v(". Blankinship, “The Early Creed,” p. 49.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn165",
      "id": "ft165"
    }
  }, [_vm._v("165")]), _vm._v(". Blankinship, “The Early Creed,” p. 50.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn166",
      "id": "ft166"
    }
  }, [_vm._v("166")]), _vm._v(". Hermansen, “Eschatology,” pp. 318–19; Watt, "), _c('em', [_vm._v("Islamic Philosophy and Theology")]), _vm._v(", pp. 51–52.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn167",
      "id": "ft167"
    }
  }, [_vm._v("167")]), _vm._v(". See "), _c('em', [_vm._v("Sahih Bukhari")]), _vm._v(", vol. 9, bk. 93 (“Oneness, Uniqueness of Allah [Tawheed]”), #532A, B, C.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn168",
      "id": "ft168"
    }
  }, [_vm._v("168")]), _vm._v(". For a rich account of visions and images of the afterlife, see Rustomji, "), _c('em', [_vm._v("The Garden and the Fire")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn169",
      "id": "ft169"
    }
  }, [_vm._v("169")]), _vm._v(". D. Peterson, “Eschatology.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn170",
      "id": "ft170"
    }
  }, [_vm._v("170")]), _vm._v(". See Afsaruddin, “Death, Resurrection, and Human Destiny,” pp. 51–54.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn171",
      "id": "ft171"
    }
  }, [_vm._v("171")]), _vm._v(". "), _c('em', [_vm._v("Sahih Bukhari")]), _vm._v(", vol. 4, bk. 54, #479; vol. 4, bk. 52, #48; see also Afsaruddin, “Death, Resurrection, and Human Destiny,” p. 47. For a contemporary poetic description, see “Abū ’l-‘Alā al-Mawdūdi on the Verse of Light,” pp. 42–47.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn172",
      "id": "ft172"
    }
  }, [_vm._v("172")]), _vm._v(". “Bāqillāni on the Vision of God in the Next Life.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn173",
      "id": "ft173"
    }
  }, [_vm._v("173")]), _vm._v(". For a text example from an Asharite Bāqillāni of the eleventh century, see “Bāqillāni on the Vision of God in the Next Life,” and from a contemporary Shi’ite, “Alī on the Vision of God,” pp. 209–11. See further Watt, "), _c('em', [_vm._v("Islamic Philosophy and Theology")]), _vm._v(", p. 66; Hermansen, “Eschatology,” p. 320.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn174",
      "id": "ft174"
    }
  }, [_vm._v("174")]), _vm._v(". Knipe, “Hindu Eschatology,” p. 171; so also Hiltebeitel, “The ‘Mahābhārata,’ and Hindu Eschatology,” p. 95.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn175",
      "id": "ft175"
    }
  }, [_vm._v("175")]), _vm._v(". Dhavamony, “Death and Immortality in Hinduism,” pp. 100–101 (100).")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn176",
      "id": "ft176"
    }
  }, [_vm._v("176")]), _vm._v(". Note that the cosmos is sometimes compared to a cosmic being, a living organism, as in the famous hymn to Purusa in Rig Veda 10.90, where out of the organs of the deity everything is made.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn177",
      "id": "ft177"
    }
  }, [_vm._v("177")]), _vm._v(". Consult chap. 7 of the Gita.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn178",
      "id": "ft178"
    }
  }, [_vm._v("178")]), _vm._v(". A highly insightful “insider” account is the short pamphlet written by Maharaj, "), _c('em', [_vm._v("Origin and Eschatology in Hindu Eschatology")]), _vm._v(". See also K. Ward, "), _c('em', [_vm._v("Religion and Revelation")]), _vm._v(", p. 141. For the radical incompatibility with this and the Abrahamic faiths, see Zaehner, "), _c('em', [_vm._v("Mysticism, Sacred and Profane")]), _vm._v(", p. 204.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn179",
      "id": "ft179"
    }
  }, [_vm._v("179")]), _vm._v(". For a useful discussion, see Doniger, "), _c('em', [_vm._v("On Hinduism")]), _vm._v(", pp. 97–103.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn180",
      "id": "ft180"
    }
  }, [_vm._v("180")]), _vm._v(". Particularly interested in "), _c('em', [_vm._v("kalpas")]), _vm._v(" are the great epics; see "), _c('em', [_vm._v("Mahabharata")]), _vm._v(" 3.148; 12.231, 341; Vishnu Purana 1.3; 3.2–3; and so forth; I am indebted to Dhavamony, “Death and Immortality in Hinduism.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn181",
      "id": "ft181"
    }
  }, [_vm._v("181")]), _vm._v(". For a basic discussion, see chap. 13 in Sharma, "), _c('em', [_vm._v("Classical Hindu Thought")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn182",
      "id": "ft182"
    }
  }, [_vm._v("182")]), _vm._v(". Dhavamony, “Death and Immortality in Hinduism,” pp. 100–101 (101).")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn183",
      "id": "ft183"
    }
  }, [_vm._v("183")]), _vm._v(". Bhagavad-Gita 4.7–8; for details, see my "), _c('em', [_vm._v("Christ and Reconciliation")]), _vm._v(", pp. 146–48.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn184",
      "id": "ft184"
    }
  }, [_vm._v("184")]), _vm._v(". Urban, “Millenarian Elements in the Hindu Religious Traditions,” p. 369. A classic, still useful study is S. Fuchs, "), _c('em', [_vm._v("Rebellious Prophets")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn185",
      "id": "ft185"
    }
  }, [_vm._v("185")]), _vm._v(". For important observations, see Knipe, “Hindu Eschatology,” pp. 172–74.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn186",
      "id": "ft186"
    }
  }, [_vm._v("186")]), _vm._v(". It looks to me that at times Yama is spoken of as the first "), _c('em', [_vm._v("human")]), _vm._v(" to ever die; at other times, Yama is named the first god to taste death.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn187",
      "id": "ft187"
    }
  }, [_vm._v("187")]), _vm._v(". See Rig Veda 10.15; for comments, see Knipe, “Hindu Eschatology,” pp. 174–75.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn188",
      "id": "ft188"
    }
  }, [_vm._v("188")]), _vm._v(". For many details of what happens after death, see Rig Veda 10.15–18 (the last hymn of which is also a funeral prayer). See further Dhavamony, “Death and Immortality in Hinduism,” pp. 93–94.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn189",
      "id": "ft189"
    }
  }, [_vm._v("189")]), _vm._v(". Katha Upanishad 2.1; similarly two destinies, based on one’s deeds, are taught in the "), _c('em', [_vm._v("Mahabharata")]), _vm._v(", bk. 11 ("), _c('em', [_vm._v("Stri Parva")]), _vm._v("), #3; Bhagavad-Gita 8.24–25.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn190",
      "id": "ft190"
    }
  }, [_vm._v("190")]), _vm._v(". See Doniger, "), _c('em', [_vm._v("On Hinduism")]), _vm._v(", p. 89; K. Ward, "), _c('em', [_vm._v("Religion and Human Nature")]), _vm._v(", pp. 57–58.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn191",
      "id": "ft191"
    }
  }, [_vm._v("191")]), _vm._v(". Often the first destination is the sun (Rig Veda 10.16.3; Atharva Veda 18.2.7); other times the moon (Kaushitaki Upanishad 1.1–4).")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn192",
      "id": "ft192"
    }
  }, [_vm._v("192")]), _vm._v(". A highly important and detailed description of the soul’s transmigration can be found in Brihadaranyaka Upanishad 4.3.1–4.4.2; see also Chandogya Upanishad 6.8.6. For comments, consult further Dhavamony, “Death and Immortality in Hinduism,” pp. 94–95.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn193",
      "id": "ft193"
    }
  }, [_vm._v("193")]), _vm._v(". For supportive scholarly documentation, see Milner, “Hindu Eschatology and the Indian Caste System,” p. 298. For a useful discussion based on the Bhagavata Purana’s description of "), _c('em', [_vm._v("jiva")]), _vm._v("’s (human self or living being) journey over many lives, see chap. 11 in Sharma, "), _c('em', [_vm._v("Classical Hindu Thought")]), _vm._v(". On the differences between the Hindu and Buddhist notion of samsara, see Reat, “Karma and Rebirth in the "), _c('em', [_vm._v("Upanisads")]), _vm._v(" and Buddhism.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn194",
      "id": "ft194"
    }
  }, [_vm._v("194")]), _vm._v(". B. Smith, “"), _c('em', [_vm._v("Samsāra")]), _vm._v(",” p. 8098.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn195",
      "id": "ft195"
    }
  }, [_vm._v("195")]), _vm._v(". Doniger, "), _c('em', [_vm._v("On Hinduism")]), _vm._v(", p. 93 (more widely, chap. 12). For the reasons Hindus give to support transmigration (rebirth), such as certain innate predilections (e.g., a baby sucking the mother’s milk or the innate fear of death), see Balasubramanian, “The Advaita View of Death and Immortality,” p. 123.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn196",
      "id": "ft196"
    }
  }, [_vm._v("196")]), _vm._v(". Dhavamony, “Death and Immortality in Hinduism,” p. 94. A highly important and detailed description of the soul’s transmigration can be found in Brihadaranyaka Upanishad 4.3.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn197",
      "id": "ft197"
    }
  }, [_vm._v("197")]), _vm._v(". Death at a mature age may also be considered a “good” death. See further A. King, “The Glorious Disappearance of Vaishnavas,” p. 105.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn198",
      "id": "ft198"
    }
  }, [_vm._v("198")]), _vm._v(". On death’s inevitable nature as something created by God, see the "), _c('em', [_vm._v("Mahabharata")]), _vm._v(", bk. 7 ("), _c('em', [_vm._v("Drona Parva")]), _vm._v("), ##52–54; Bhagavad-Gita 2.27–28. Because of its appearance ("), _c('em', [_vm._v("maya")]), _vm._v(") nature, “The wise grieve neither for the living nor for the dead” (2.11). Note that the “appearance” nature of reality (as distinct from the “real” one) does not of course mean that the cosmos would not exist; even things of appearance nature do exist. It means that it is “secondary” in importance to the real one. For basics of "), _c('em', [_vm._v("maya")]), _vm._v(", see chap. 14 in Sharma, "), _c('em', [_vm._v("Classical Hindu Thought")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn199",
      "id": "ft199"
    }
  }, [_vm._v("199")]), _vm._v(". Katha Upanishad 1.2.18; see further Dhavamony, “Death and Immortality in Hinduism,” pp. 94–95. For similar teaching in the Gita, see 2.12.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn200",
      "id": "ft200"
    }
  }, [_vm._v("200")]), _vm._v(". Dhavamony, “Death and Immortality in Hinduism,” p. 93.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn201",
      "id": "ft201"
    }
  }, [_vm._v("201")]), _vm._v(". A representative Upanishadic passage is Brihadaranyaka Upanishad 4.3.36; see further Rambachan, “Hinduism,” pp. 74–75.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn202",
      "id": "ft202"
    }
  }, [_vm._v("202")]), _vm._v(". Bhagavad-Gita 8.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn203",
      "id": "ft203"
    }
  }, [_vm._v("203")]), _vm._v(". Dhavamony, “Death and Immortality in Hinduism,” pp. 97–98 (98); see Mundaka Upanishad 3.2.9.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn204",
      "id": "ft204"
    }
  }, [_vm._v("204")]), _vm._v(". K. Ward, "), _c('em', [_vm._v("Religion and Human Nature")]), _vm._v(", pp. 74, 75.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn205",
      "id": "ft205"
    }
  }, [_vm._v("205")]), _vm._v(". For basic ideas, see my "), _c('em', [_vm._v("Creation and Humanity")]), _vm._v(", pp. 378–81.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn206",
      "id": "ft206"
    }
  }, [_vm._v("206")]), _vm._v(". The principle of the “appearance” nature of reality also has bearing on anthropology: “The apparent man is merely a struggle to express, to manifest this individuality which is beyond.” From Vivekananda, “The Real Nature of Man,” in Adiswarananda, "), _c('em', [_vm._v("Vivekananda")]), _vm._v(", p. 83.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn207",
      "id": "ft207"
    }
  }, [_vm._v("207")]), _vm._v(". Brihadaranyaka Upanishad 4.4.5.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn208",
      "id": "ft208"
    }
  }, [_vm._v("208")]), _vm._v(". For accessible basic accounts, see Balasubramanian, “The Advaita View of Death and Immortality,” pp. 110–17; Rambachan, “Hinduism,” pp. 71–74.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn209",
      "id": "ft209"
    }
  }, [_vm._v("209")]), _vm._v(". For the "), _c('em', [_vm._v("advaita")]), _vm._v(" school’s (Sankara) detailed analysis of the effects of "), _c('em', [_vm._v("avidya")]), _vm._v(", that is, “superimposition,” see Sankara, "), _c('em', [_vm._v("Commentary on Vedanta Sutras")]), _vm._v(", part I, 1.1.1; "), _c('em', [_vm._v("SBE")]), _vm._v(" 34:4; for exposition, see my "), _c('em', [_vm._v("Creation and Humanity")]), _vm._v(", pp. 422–23.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn210",
      "id": "ft210"
    }
  }, [_vm._v("210")]), _vm._v(". The Christian interpreter should not see here any kind of “Fall” or original sinfulness though. See Thatamanil, "), _c('em', [_vm._v("Immanent Divine")]), _vm._v(", p. 36.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn211",
      "id": "ft211"
    }
  }, [_vm._v("211")]), _vm._v(". For a detailed discussion, see my "), _c('em', [_vm._v("Creation and Humanity")]), _vm._v(", pp. 421–25.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn212",
      "id": "ft212"
    }
  }, [_vm._v("212")]), _vm._v(". See, e.g., Isa Upanishad 5.9; see further Organ, "), _c('em', [_vm._v("The Hindu Quest for the Perfection of Man")]), _vm._v(", p. 5.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn213",
      "id": "ft213"
    }
  }, [_vm._v("213")]), _vm._v(". Radhakrishnan, "), _c('em', [_vm._v("Indian Philosophy")]), _vm._v(", 2:26. For a lucid discussion, see also Sivaraman, “The Meaning of "), _c('em', [_vm._v("Moksha")]), _vm._v(",” pp. 2–11.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn214",
      "id": "ft214"
    }
  }, [_vm._v("214")]), _vm._v(". The standard discussion is Hiltebeitel, “The ‘Mahābhārata,’ and Hindu Eschatology.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn215",
      "id": "ft215"
    }
  }, [_vm._v("215")]), _vm._v(". Doniger, "), _c('em', [_vm._v("On Hinduism")]), _vm._v(", pp. 94–95; for a highly useful analysis, see Jacobsen, “Three Functions of Hell in the Hindu Traditions.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn216",
      "id": "ft216"
    }
  }, [_vm._v("216")]), _vm._v(". For details, see Doniger, "), _c('em', [_vm._v("On Hinduism")]), _vm._v(", pp. 95–98; Dhavamony, “Death and Immortality in Hinduism,” pp. 104–5.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn217",
      "id": "ft217"
    }
  }, [_vm._v("217")]), _vm._v(". Werblowsky, “Eschatology,” p. 2834.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn218",
      "id": "ft218"
    }
  }, [_vm._v("218")]), _vm._v(". Just see the famous discussion “IV. Speculations about the Future ("), _c('em', [_vm._v("Aparantakappika")]), _vm._v("),” in "), _c('em', [_vm._v("Brahmajāla Sutta: The All-Embracing Net of Views; Digha-Nikaya")]), _vm._v(" 1.74–130; see also Hick, "), _c('em', [_vm._v("Death and Eternal Life")]), _vm._v(", p. 21.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn219",
      "id": "ft219"
    }
  }, [_vm._v("219")]), _vm._v(". "), _c('em', [_vm._v("Majjhima Nikaya Sutta")]), _vm._v(" 63.8, p. 536; see de Silva, “The Buddhist Attitude towards Nature,” n.p. See also the important comments by the Japanese Christian theologian Koyama, "), _c('em', [_vm._v("Mount Fuji and Mount Sinai")]), _vm._v(", p. 74.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn220",
      "id": "ft220"
    }
  }, [_vm._v("220")]), _vm._v(". For details and documentation, see Nattier, “Buddhist Eschatology,” pp. 152–53.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn221",
      "id": "ft221"
    }
  }, [_vm._v("221")]), _vm._v(". "), _c('em', [_vm._v("Lotus Sutra")]), _vm._v(" 5.80; 13.19.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn222",
      "id": "ft222"
    }
  }, [_vm._v("222")]), _vm._v(". "), _c('em', [_vm._v("Saticca-samuppada-vibhanga Sutta")]), _vm._v(": "), _c('em', [_vm._v("Analysis of Dependent Co-arising")]), _vm._v(" (Samyutta Nikaya 12.2); a classic statement is in Samyutta Nikaya 12.61; for details, see Payutto, "), _c('em', [_vm._v("Dependent Origination")]), _vm._v(", “Introduction,” n.p.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn223",
      "id": "ft223"
    }
  }, [_vm._v("223")]), _vm._v(". de Silva, “The Buddhist Attitude towards Nature,” n.p. The Pali term "), _c('em', [_vm._v("anicca")]), _vm._v(" is routinely used to describe this principle.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn224",
      "id": "ft224"
    }
  }, [_vm._v("224")]), _vm._v(". The authoritative statement is that of Buddhagosa, "), _c('em', [_vm._v("Visuddhi Visuddhimagga")]), _vm._v(" 16.71, p. 522. For a detailed account of the Buddhist view of “creation” and its relation to Christian view, see my "), _c('em', [_vm._v("Creation and Humanity")]), _vm._v(", pp. 86–91.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn225",
      "id": "ft225"
    }
  }, [_vm._v("225")]), _vm._v(". For details, see Nattier, “Buddhist Eschatology,” pp. 154–55.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn226",
      "id": "ft226"
    }
  }, [_vm._v("226")]), _vm._v(". Buddha’s “Setting the Wheel of Dhamma in Motion” can be found, e.g., in "), _c('em', [_vm._v("Dhammacakkappavattana Sutta")]), _vm._v(" 11 of Samyutta Nikaya 56.11. The four noble truths are called “the teaching special to the Buddhas” in Majjhima Nikaya 56.18 ("), _c('em', [_vm._v("The Middle Length Discourses")]), _vm._v(", p. 485).")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn227",
      "id": "ft227"
    }
  }, [_vm._v("227")]), _vm._v(". Chandngarm, "), _c('em', [_vm._v("Arriyasatsee")]), _vm._v(", pp. 9–14.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn228",
      "id": "ft228"
    }
  }, [_vm._v("228")]), _vm._v(". Similarly to the notion of "), _c('em', [_vm._v("dukkha")]), _vm._v(", the term "), _c('em', [_vm._v("tanhā")]), _vm._v(" (“craving” or “desire”) used by Buddha is a multifaceted concept. A highly useful account of "), _c('em', [_vm._v("tanhā")]), _vm._v(", based on Majjhima Nikaya, is Ňānamoli and Bodhi, introduction to "), _c('em', [_vm._v("The Middle Length Discourses of the Buddha")]), _vm._v(", pp. 27–29.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn229",
      "id": "ft229"
    }
  }, [_vm._v("229")]), _vm._v(". A highly important and accessible account of no-self with basic scriptural references (from the standard manual of Majjhima Nikaya) is given in Ňānamoli and Bodhi, introduction to "), _c('em', [_vm._v("The Middle Length Discourses of the Buddha")]), _vm._v(", pp. 27–29.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn230",
      "id": "ft230"
    }
  }, [_vm._v("230")]), _vm._v(". An authoritative study is S. Collins, "), _c('em', [_vm._v("Selfless Persons.")])]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn231",
      "id": "ft231"
    }
  }, [_vm._v("231")]), _vm._v(". "), _c('em', [_vm._v("Visuddhimagga")]), _vm._v(", chap. 19, pp. 627–28 (627).")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn232",
      "id": "ft232"
    }
  }, [_vm._v("232")]), _vm._v(". Ňānamoli and Bodhi, introduction to "), _c('em', [_vm._v("The Middle Length Discourses of the Buddha")]), _vm._v(", p. 45.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn233",
      "id": "ft233"
    }
  }, [_vm._v("233")]), _vm._v(". Ňānamoli and Bodhi, introduction to "), _c('em', [_vm._v("The Middle Length Discourses of the Buddha")]), _vm._v(", p. 45.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn234",
      "id": "ft234"
    }
  }, [_vm._v("234")]), _vm._v(". "), _c('em', [_vm._v("Alagaddupama Sutta: The Water-Snake Simile")]), _vm._v(" of Majjhima Nikaya 22.38 ("), _c('em', [_vm._v("The Middle Length Discourses")]), _vm._v(", p. 234).")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn235",
      "id": "ft235"
    }
  }, [_vm._v("235")]), _vm._v(". “Nibbana Sutta: Total Unbinding (1)” ("), _c('em', [_vm._v("Udana")]), _vm._v(" 8.1).")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn236",
      "id": "ft236"
    }
  }, [_vm._v("236")]), _vm._v(". For standard scriptural exposition of the Eightfold Path, see Majjhina Nikaya 141 and 117; for reliable discussion, see Ňānamoli and Bodhi, introduction to "), _c('em', [_vm._v("The Middle Length Discourses of the Buddha")]), _vm._v(", pp. 32–34.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn237",
      "id": "ft237"
    }
  }, [_vm._v("237")]), _vm._v(". Rahula, "), _c('em', [_vm._v("What the Buddha Taught")]), _vm._v(", pp. 1–2. For the high praises of the Buddha as teacher, mentor pointing to release, see Majjhima Nikaya 108.5 ("), _c('em', [_vm._v("The Middle Length Discourses")]), _vm._v(", p. 880).")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn238",
      "id": "ft238"
    }
  }, [_vm._v("238")]), _vm._v(". "), _c('em', [_vm._v("Dhammapada")]), _vm._v(" 12.4; "), _c('em', [_vm._v("SBE")]), _vm._v(" 10 (in some other versions, 12.160, when verses are numbered from the beginning of the work, rather than from the beginning of each chapter). The English translation of this passage varies; I have followed here the one adopted by Rahula, "), _c('em', [_vm._v("What the Buddha Taught")]), _vm._v(", p. 1. Similarly, "), _c('em', [_vm._v("Dhammapada")]), _vm._v(" 20.4 (20.276) puts it: “You yourselves must strive; the Buddhas only point the way.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn239",
      "id": "ft239"
    }
  }, [_vm._v("239")]), _vm._v(". For an analysis of the stages, see Ňānamoli and Bodhi, introduction to "), _c('em', [_vm._v("The Middle Length Discourses of the Buddha")]), _vm._v(", pp. 41–43 (41).")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn240",
      "id": "ft240"
    }
  }, [_vm._v("240")]), _vm._v(". A basic source is "), _c('em', [_vm._v("A Manual of Abhidhamma (Abhidhammattha Sangaha)")]), _vm._v(". Another authoritative statement is Buddhagosa, "), _c('em', [_vm._v("Visuddhi Visuddhimagga")]), _vm._v(" 16.71, p. 522.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn241",
      "id": "ft241"
    }
  }, [_vm._v("241")]), _vm._v(". “Nibbana Sutta: Total Unbinding (1)” ("), _c('em', [_vm._v("Udana")]), _vm._v(" 8.1). Another classic description is Majjhima Nikaya 26.13 ("), _c('em', [_vm._v("The Middle Length Discourses")]), _vm._v(", p. 256). For an important contemporary discussion, see Dharmasiri, "), _c('em', [_vm._v("A Buddhist Critique of the Christian Concept of God")]), _vm._v(", pp. 177–214. The discussion includes the relation to the Hindu use of the concept as well as critique of Christian responses and interpretations.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn242",
      "id": "ft242"
    }
  }, [_vm._v("242")]), _vm._v(". See also K. Ward, "), _c('em', [_vm._v("Images of Eternity")]), _vm._v(", pp. 61–62.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn243",
      "id": "ft243"
    }
  }, [_vm._v("243")]), _vm._v(". "), _c('em', [_vm._v("The Lankavatara Sutra: A Mahayana Text")]), _vm._v(" 2.7.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn244",
      "id": "ft244"
    }
  }, [_vm._v("244")]), _vm._v(". Majjhima Nikaya 35.25 (p. 596); for other descriptions, see 22.30–35 ("), _c('em', [_vm._v("The Middle Length Discourses")]), _vm._v(", pp. 233–34); 39.22–29 (pp. 37–71); for details, see Ňānamoli and Bodhi, introduction to "), _c('em', [_vm._v("The Middle Length Discourses of the Buddha")]), _vm._v(", pp. 43–45.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn245",
      "id": "ft245"
    }
  }, [_vm._v("245")]), _vm._v(". So, e.g., Nattier, “Buddhist Eschatology,” p. 157.")])])])
}]}


//////////////////
// WEBPACK FOOTER
// ../node_modules/vue-loader/lib/template-compiler?{"id":"data-v-45fc6d7a","hasScoped":false,"transformToRequire":{"video":"src","source":"src","img":"src","image":"xlink:href"}}!../node_modules/vue-loader/lib/selector.js?type=template&index=0!./src/components/011_Chapter03.vue
// module id = 87
// module chunks = 0